import { Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowLeftRight, ArrowUp, ClipboardPaste, Copy, Paperclip, Trash2, X } from 'lucide-react'
import { rechenwegText, rechneArbeitsschritt, rechneKostenzeile, type LineCostKind } from '@symphenia/calc-core'
import { erfuelltRecht, geldText, preisText } from '@symphenia/shared-types'
import { Feld, RasterFeld, RasterInfo, Vorschlaege, ZeilenBlock, ZeilenListe, useListenBreite, useSuche, zelle, type Spalte } from '@/components/zeilenblock'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { KlappUmschalter, useKlappModus, type KlappModus } from '@/components/klappmodus'
import { ZiehBereich, useRandMarkieren, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeMehrereVor } from '@/components/ziehen/ziehen'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import {
  eingabeOffen,
  gleich,
  mitStand,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  useStellenDaten,
  zahlText,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { api } from '@/lib/axios'
import {
  convertCostLine,
  copyCostLines,
  costLineUrl,
  createCostLine,
  fetchClipboardArticleIds,
  pasteCostLines,
  createWorkStep,
  deleteCostLine,
  deleteWorkStep,
  fetchSheet,
  pasteWorkSteps,
  reorderCostLines,
  reorderWorkSteps,
  searchCostArticles,
  searchSuppliers,
  surchargeUrl,
  workStepUrl,
} from '../api/calculation-sheet.api'
import { fetchCalcBomCategories, fetchCalcPhases, fetchCalcQualifications, fetchCalcWorkStepTemplates } from '../api/calculation-settings.api'
import { KOSTENARTEN as ALLE_KOSTENARTEN, KOSTENART_TEXT, gewichtText, stundenText } from '../lib/kalkulation-labels'
import { beobachteSchrittAblage, leseSchrittAblage, schreibeSchrittAblage, type SchrittAblage } from '../lib/schritt-ablage'
import { KalkulationsDateien, dateienText, haengeAn } from './KalkulationsDateien'
import { ErfahrungDialog } from './ErfahrungDialog'
import { RichtwertDialog } from './RichtwertDialog'
import { AngeboteUebernehmen } from './anfragen/AngeboteUebernehmen'
import { UebernehmenDialog } from './anfragen/UebernehmenDialog'
import type {
  CalcBomCategory,
  CalcRequestMark,
  CalcCostArticle,
  CalcCostLine,
  CalcLineCostKind,
  CalcPhase,
  CalcPosition,
  CalcQuantityBasis,
  CalcQualification,
  CalcSheet,
  CalcSheetRate,
  CalcUnit,
  CalcWorkStep,
  CalcWorkStepTemplate,
  Calculation,
} from '../types/calculation.types'

/**
 * Ab dieser Breite des Blatts steht die Ergebnisspalte neben den Rastern, ohne dass sie umbrechen (Nachtrag 1):
 * Ergebnisspalte `w-72` (288 px) + Abstand 16 px + das breitere der beiden Raster (Arbeitsschritte, 668 px)
 * mit etwas Luft. Die Schwelle ist dieselbe wie vor Nachtrag 6.
 */
const BLATT_MIT_ERGEBNIS = 1000
/** Ab hier hat die Ergebnisspalte Platz für `w-80` — ihre zwei Zahlenspalten (N6-6) stehen dann lockerer. */
const BLATT_MIT_BREITEM_ERGEBNIS = 1200
/**
 * Nachtrag 8 (N8-6, N8-10) — ab dieser Breite der **eigenen Liste** gilt die breite Anordnung: In „Material &
 * Einzelkosten" stehen Kategorie, Verschnitt, PE und Rabatt in der ersten Zeile, in den Arbeitsschritten die
 * Beschreibung. Am Nachbau im Originalmaß abgestimmt; darunter bleibt die Anordnung, wie sie war (N8-11).
 */
const KOSTEN_BREIT = 1116
const SCHRITTE_BREIT = 1162

const KOSTENARTEN: { wert: CalcLineCostKind; text: string }[] = [
  { wert: 'MATERIAL', text: 'Material' },
  { wert: 'SUBCONTRACT', text: 'Fremdleistung' },
  { wert: 'EQUIPMENT', text: 'Gerät' },
  { wert: 'OTHER', text: 'Sonstiges' },
]
const BEZUG = [
  { wert: 'PER_UNIT', text: 'je E' },
  { wert: 'TOTAL', text: 'gesamt' },
]
const HERKUNFT: Record<string, string> = { TEMPLATE: 'aus Vorlage', QUICK: 'Schnellspalte', AI: 'Vorschlag' }

/**
 * AP-39 Nachtrag 2 (6.1) — was die Marke der Anfrage an einer Materialzeile tun kann: das beste Angebot übernehmen
 * (mit Vorschau) und alle Angebote der Kalkulation vergleichen. Das Blatt hält die Fenster; die Zeilen liegen zwei
 * Ebenen tiefer und bekommen die Handlungen über diesen Kontext. `null` heißt: nur lesen.
 */
const AngebotsKontext = createContext<{ uebernehmen?: (costLineId: string, requestLineId: string) => void; vergleichen?: () => void } | null>(null)

const zahl = (n: number | null | undefined, stellen = 2) =>
  n == null ? '' : n.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen })
const menge = (n: number | null | undefined) => (n == null ? '' : n.toLocaleString('de-DE', { maximumFractionDigits: 3 }))
const nurAnzeige = (text: string, rechts = true) => <span className={`block truncate px-1.5 py-1 text-sm tabular-nums ${rechts ? 'text-right' : ''}`}>{text}</span>
/** Stunden je Einheit sind oft Bruchteile („0,35 h je lfm“) — eine Stelle wie bei den Summen sagte dort zu wenig. */
const stundenGenau = (h: number) => h.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 3 })

/**
 * Nachtrag 6 (N6-3) — ein Wert **je Einheit der Position**: gesamt ÷ Menge. Eine Aufzahlung ohne Menge
 * (N1-29) rechnet der Rechenkern wie für eine Einheit — dann gilt hier dasselbe (`fuerEine`).
 */
const jeEinheitWert = (gesamt: number, positionsMenge: number, fuerEine: () => number) => (positionsMenge !== 0 ? gesamt / positionsMenge : fuerEine())

/**
 * Nachtrag 6 (N6-3) — die **Schlusszeile** einer Liste des Blatts: links die Zahl der Zeilen, rechts je Größe
 * (Gewicht, Stunden, Kosten) der Wert je Einheit der Position und daneben der Wert gesamt.
 */
function Schlusszeile({ links, jeEinheit, werte }: { links: string; jeEinheit: string; werte: { titel: string; je: string; gesamt: string; fett?: boolean }[] }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-t border-border bg-muted/40 px-3 py-1.5 text-sm" data-schlusszeile>
      <span>{links}</span>
      <span className="flex flex-wrap items-baseline justify-end gap-x-6 gap-y-1">
        {werte.map((w) => (
          <span key={w.titel} className="whitespace-nowrap tabular-nums">
            <span className="text-muted-foreground">
              {w.titel} {jeEinheit}
            </span>{' '}
            {w.je} <span className="text-muted-foreground">· gesamt</span> <span className={w.fett ? 'font-medium' : ''}>{w.gesamt}</span>
          </span>
        ))}
      </span>
    </div>
  )
}

/**
 * AP-37 4.3, 4.4, 9. — **das Kalkulationsblatt** einer Position: oben Material
 * und Einzelkosten, darunter die Arbeitsschritte, rechts die feste
 * Ergebnisspalte (Einzelkosten je Kostenart, Aufschläge mit Abweichung, GP
 * kalkuliert, EP, EP von Hand, Lohn/Sonstiges, Stunden, DB).
 *
 * Jede Zeile ist eine eigene Speicherstelle mit eigenem Stand (AP-36): Sie
 * speichert beim Verlassen, die leere letzte Zeile legt an. Nach jeder Änderung
 * lädt das Blatt neu, weil Bedarf, Kosten und die Ergebnisspalte am Server
 * gerechnet werden (`calc-core`).
 *
 * Ohne `calculation.cost.view` ist das Blatt nur lesbar und ohne Preise.
 */
export function KalkulationsBlatt({
  kalkulation,
  position,
  stelle,
  einheiten,
  onGeaendert,
}: {
  kalkulation: Calculation
  position: CalcPosition
  /** Speicherstelle der Position (EP von Hand) — dieselbe wie im Kopf. */
  stelle: Speicherstelle<unknown>
  einheiten: CalcUnit[]
  onGeaendert: () => void
}) {
  const { user } = useAuthStore()
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  const darf = kalkulation.writable && kosten && erfuelltRecht('calculation.calculation.manage', user?.permissions) && position.kind !== 'TEXT'
  // Detailblatt O 4a.3: Dateien anhängen verlangt `manage`, nicht `cost.view` — eine Datei ist kein Preis.
  const darfDateien = kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const [blatt, setBlatt] = useState<CalcSheet | null>(null)
  const [phasen, setPhasen] = useState<CalcPhase[]>([])
  const [qualis, setQualis] = useState<CalcQualification[]>([])
  const [vorlagen, setVorlagen] = useState<CalcWorkStepTemplate[]>([])
  const [kategorien, setKategorien] = useState<CalcBomCategory[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [neuK, setNeuK] = useState(0)
  const [neuS, setNeuS] = useState(0)
  const [fokusK, setFokusK] = useState(false)
  const [fokusS, setFokusS] = useState(false)
  // Nachtrag 7 (N7-11): wie die Zeilen der beiden Listen aufklappen — je Liste und Benutzer gemerkt.
  const [klappKosten, setKlappKosten] = useKlappModus('calc_costs')
  const [klappSchritte, setKlappSchritte] = useKlappModus('calc_steps')
  // AP-39 Nachtrag 2 (6.1): das beste Angebot einer Materialzeile übernehmen bzw. alle der Kalkulation vergleichen.
  // Vergleichen braucht nur `cost.view`, übernehmen dazu das Bearbeiten (wie „Preise übernehmen" im Cockpit).
  const [uebernahme, setUebernahme] = useState<{ costLineId: string; requestLineId: string } | null>(null)
  const [vergleich, setVergleich] = useState(false)
  const angebote = useMemo(
    () =>
      kosten
        ? { vergleichen: () => setVergleich(true), ...(darf ? { uebernehmen: (costLineId: string, requestLineId: string) => setUebernahme({ costLineId, requestLineId }) } : {}) }
        : null,
    [kosten, darf],
  )

  const laden = useCallback(async () => {
    try {
      setBlatt(await fetchSheet(kalkulation.id, position.id))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Blatt ließ sich nicht laden.'))
    }
  }, [kalkulation.id, position.id])
  // N1-35: Ändert sich die Position außerhalb des Blatts (EP von Hand, Menge im Kopf), stimmt das
  // Ergebnis nicht mehr — neu laden, sobald ihr Stand nicht mehr der des Blatts ist.
  const blattStand = blatt?.position.updatedAt
  useEffect(() => {
    if (blattStand && position.updatedAt && position.updatedAt !== blattStand) void laden()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.updatedAt])
  useEffect(() => {
    void laden()
    fetchCalcPhases().then(setPhasen).catch(() => setPhasen([]))
    fetchCalcQualifications().then(setQualis).catch(() => setQualis([]))
    fetchCalcWorkStepTemplates().then(setVorlagen).catch(() => setVorlagen([]))
    fetchCalcBomCategories().then(setKategorien).catch(() => setKategorien([]))
  }, [laden])

  // Nachtrag 1: Das Blatt steht neben der Positionsliste und kennt die Fensterbreite nicht —
  // die Ergebnisspalte richtet sich nach der **eigenen** Breite. Passt sie nicht neben die
  // Raster, rückt sie darunter, statt die Raster in die gestapelte Form zu zwingen.
  const [breite, setBreite] = useState(0)
  const beobachter = useRef<ResizeObserver | null>(null)
  const messen = useCallback((el: HTMLDivElement | null) => {
    beobachter.current?.disconnect()
    if (!el) return
    setBreite(el.clientWidth)
    beobachter.current = new ResizeObserver(([e]) => setBreite(e.contentRect.width))
    beobachter.current.observe(el)
  }, [])
  const ergebnisDaneben = breite >= BLATT_MIT_ERGEBNIS

  // Die Position im Kopf rechnet mit: Nach jeder Zeile lädt sie neu (EP, GP, Zustand).
  const geaendert = useCallback(() => {
    void laden()
    onGeaendert()
  }, [laden, onGeaendert])

  const tu = async (schritt: () => Promise<unknown>) => {
    setFehler(null)
    try {
      await schritt()
      geaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  if (!blatt) return <p className="text-sm text-muted-foreground">{fehler ?? 'Lädt …'}</p>
  if (position.kind === 'TEXT') return <p className="text-sm text-muted-foreground">Eine Textposition hat kein Kalkulationsblatt.</p>

  // Nachtrag 6 (N6-3): „je Einheit“ nennt die Einheit der Position — „je lfm“ sagt mehr als „je E“.
  const jeEinheit = `je ${position.unitCode ?? position.unitRaw ?? 'Einheit'}`

  return (
    <AngebotsKontext.Provider value={angebote}>
    {uebernahme && (
      <UebernehmenDialog
        choices={[uebernahme]}
        onClose={() => setUebernahme(null)}
        onFertig={() => {
          setUebernahme(null)
          geaendert()
        }}
      />
    )}
    {vergleich && <AngeboteUebernehmen kalkulation={kalkulation} onClose={() => setVergleich(false)} onGeaendert={geaendert} />}
    <div ref={messen} className={`flex gap-4 ${ergebnisDaneben ? 'flex-row items-start' : 'flex-col'}`}>
      <div className="min-w-0 flex-1 space-y-4">
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        {!kosten && <p className="text-xs text-muted-foreground">Ohne das Recht, Kosten zu sehen, ist das Blatt nur lesbar — Preise und Kosten fehlen.</p>}
        <KostenListe
          kalkulationId={kalkulation.id}
          positionId={position.id}
          zeilen={blatt.costLines}
          positionsMenge={blatt.position.quantity}
          jeEinheit={jeEinheit}
          klappModus={klappKosten}
          onKlappModus={setKlappKosten}
          einheiten={einheiten}
          kategorien={kategorien}
          vorgabeBezug={blatt.costLineQuantityBasisDefault ?? 'PER_UNIT'}
          darf={darf}
          darfDateien={darfDateien}
          kosten={kosten}
          neuSchluessel={neuK}
          neuFokus={fokusK}
          onGeaendert={geaendert}
          onAngelegt={(perTastatur) => {
            setFokusK(perTastatur)
            setNeuK((k) => k + 1)
            geaendert()
          }}
          onFehler={setFehler}
          onOrdnen={(ids) => tu(() => reorderCostLines(kalkulation.id, position.id, ids))}
          onLoeschen={(z) => tu(() => deleteCostLine(kalkulation.id, position.id, z.id))}
        />
        <SchrittListe
          kalkulationId={kalkulation.id}
          positionId={position.id}
          zeilen={blatt.workSteps}
          positionsMenge={blatt.position.quantity}
          jeEinheit={jeEinheit}
          klappModus={klappSchritte}
          onKlappModus={setKlappSchritte}
          herkunft={`${kalkulation.label} · Pos. ${position.displayNumber}`}
          phasen={phasen}
          qualis={qualis}
          vorlagen={vorlagen}
          saetze={blatt.rates ?? []}
          verrechnung={blatt.laborMode === 'BILLING_RATE'}
          vorgabeQuali={blatt.defaultQualificationCode}
          standardsaetze={blatt.phaseQualifications}
          vorgabeBezug={blatt.workStepQuantityBasisDefault ?? 'PER_UNIT'}
          positionsEinheit={position.unitCode ?? position.unitRaw ?? null}
          darf={darf}
          kosten={kosten}
          neuSchluessel={neuS}
          neuFokus={fokusS}
          onGeaendert={geaendert}
          onAngelegt={(perTastatur) => {
            setFokusS(perTastatur)
            setNeuS((k) => k + 1)
            geaendert()
          }}
          onFehler={setFehler}
          onOrdnen={(ids) => tu(() => reorderWorkSteps(kalkulation.id, position.id, ids))}
          onLoeschen={(s) => tu(() => deleteWorkStep(kalkulation.id, position.id, s.id))}
        />
      </div>
      <Ergebnisspalte
        kalkulationId={kalkulation.id}
        blatt={blatt}
        stelle={stelle}
        phasen={phasen}
        jeEinheit={jeEinheit}
        darf={darf}
        kosten={kosten}
        daneben={ergebnisDaneben}
        breit={breite >= BLATT_MIT_BREITEM_ERGEBNIS}
        onGeaendert={geaendert}
      />
    </div>
    </AngebotsKontext.Provider>
  )
}

/// ── Material und Einzelkosten (4.3) ─────────────────────────────────────────

/**
 * Nachtrag 4 (N4-2) — wie die Stückliste im Projekt: Die Zeile greift man an
 * ihrer freien Fläche, markiert sie am linken Rand (Strg: dazu, Umschalt:
 * Bereich), und markierte Zeilen wandern gemeinsam. Strg+C oder „Kopieren“
 * legt sie in die Ablage, „Aus Ablage“ fügt Artikelzeilen ans Ende an.
 */
function KostenListe({
  kalkulationId,
  positionId,
  zeilen,
  positionsMenge,
  jeEinheit,
  klappModus,
  onKlappModus,
  einheiten,
  kategorien,
  vorgabeBezug,
  darf,
  darfDateien,
  kosten,
  neuSchluessel,
  neuFokus,
  onGeaendert,
  onAngelegt,
  onFehler,
  onOrdnen,
  onLoeschen,
}: {
  kalkulationId: string
  positionId: string
  zeilen: CalcCostLine[]
  /** Menge der Position — eine Zeile „je Einheit" rechnet damit, während getippt wird. */
  positionsMenge: number
  /** „je lfm“ — Beschriftung der Werte je Einheit in der Schlusszeile. */
  jeEinheit: string
  /** N7-11: immer aufgeklappt, immer zugeklappt oder beim Bearbeiten. */
  klappModus: KlappModus
  onKlappModus: (modus: KlappModus) => void
  einheiten: CalcUnit[]
  kategorien: CalcBomCategory[]
  /** Mengenbezug der leeren Zeile (Einstellungen). */
  vorgabeBezug: CalcQuantityBasis
  darf: boolean
  darfDateien: boolean
  kosten: boolean
  neuSchluessel: number
  neuFokus: boolean
  onGeaendert: () => void
  onAngelegt: (perTastatur: boolean) => void
  onFehler: (text: string | null) => void
  onOrdnen: (ids: string[]) => void
  onLoeschen: (z: CalcCostLine) => void
}) {
  // Nachtrag 8 (N8-6): Die Liste richtet sich nach ihrer **eigenen** Breite, nicht nach dem Fenster — sie steht
  // neben der Ergebnisspalte. Ungemessen (auch in jsdom) ist die Breite 0, dann gilt die schmale Anordnung.
  const { ref: listenRef, breite: listenBreite } = useListenBreite()
  const breit = listenBreite >= KOSTEN_BREIT
  const spalten = useMemo<Spalte[]>(() => {
    const anordnung: Spalte[] = breit
      ? [
          // Nachtrag 8 (N8-6): Kategorie und Verschnitt kehren aus der zweiten Zeile zurück (ändert N6-8 in
          // dieser Breite), PE und Rabatt kommen dazu. Die Art ist breiter — „Fremdleistung" steht ganz da.
          { key: 'menge', label: 'Menge', spur: '4.5rem', min: 72, rechts: true },
          { key: 'einheit', label: 'Einheit', spur: '3.75rem', min: 60 },
          { key: 'name', label: 'Bezeichnung', spur: 'minmax(8rem,3fr)', min: 128, schmal: 'w-full' },
          { key: 'art', label: 'Art', spur: '7.25rem', min: 116 },
          { key: 'kategorie', label: 'Kategorie', spur: '5.75rem', min: 92 },
          { key: 'bezug', label: 'Bezug', spur: '4.5rem', min: 72 },
          { key: 'verschnitt', label: 'Verschn.', spur: '3.5rem', min: 56, rechts: true },
          { key: 'ek', label: 'EK', spur: '4.5rem', min: 72, rechts: true },
          { key: 'pe', label: 'PE', spur: '2.75rem', min: 44, rechts: true },
          { key: 'rabatt', label: 'Rab. %', spur: '3rem', min: 48, rechts: true },
          { key: 'kosten', label: 'Kosten', spur: '5rem', min: 80, rechts: true },
          { key: 'aktion', label: '', spur: '1.5rem', min: 24 },
        ]
      : [
          // Wie die Stückliste im Projekt: Menge, Einheit, Bezeichnung — dann Art und Bezug. Kategorie und
          // Verschnitt stehen seit Nachtrag 6 (N6-8) in der zweiten Zeile unter Art und Bezug, die Bezeichnung
          // bekommt ihre Breite. Die Spur der Art ist dafür so breit wie vorher die der Kategorie.
          { key: 'menge', label: 'Menge', spur: '4.5rem', min: 56, rechts: true },
          { key: 'einheit', label: 'Einheit', spur: '4.25rem', min: 56 },
          { key: 'name', label: 'Bezeichnung', spur: 'minmax(8rem,3fr)', min: 128, schmal: 'w-full' },
          { key: 'art', label: 'Art', spur: '6.5rem', min: 88 },
          { key: 'bezug', label: 'Bezug', spur: '4.5rem', min: 60 },
          { key: 'ek', label: 'EK', spur: '5rem', min: 64, rechts: true },
          { key: 'kosten', label: 'Kosten', spur: '5.5rem', min: 68, rechts: true },
          { key: 'aktion', label: '', spur: '1.5rem', min: 24 },
        ]
    return (
      anordnung
        .filter((s) => kosten || !['ek', 'pe', 'rabatt', 'kosten'].includes(s.key))
        // Das Raster gilt ab der Summe der `min` — die festen Spuren dürfen bis dahin schrumpfen, sonst liefe es
        // zwischen beiden Summen waagrecht über (N4-3).
        .map((s) => (s.spur.startsWith('minmax') ? s : { ...s, spur: `minmax(${s.min}px,${s.spur})` }))
    )
  }, [kosten, breit])
  const behaelter = useRef<HTMLDivElement>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [hinweis, setHinweis] = useState<string | null>(null)
  const ids = useMemo(() => zeilen.map((z) => z.id), [zeilen])
  // Eine gelöschte Zeile fällt aus der Markierung.
  useEffect(() => {
    setMarkiert((m) => {
      const n = new Set([...m].filter((id) => ids.includes(id)))
      return n.size === m.size ? m : n
    })
  }, [ids])
  const auswahl = ids.filter((id) => markiert.has(id))

  /** Mit der gegriffenen wandern alle markierten Zeilen — ist sie selbst markiert (wie N-69 neu). */
  const mitGezogen = (key: string) => (markiert.has(key) ? auswahl : [key])
  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      if (!darf) return
      const neu = verschiebeMehrereVor(ids, mitGezogen(key), vorKey)
      if (neu !== ids) onOrdnen([...neu])
    },
  })

  async function tu(schritt: () => Promise<string>) {
    onFehler(null)
    setHinweis(null)
    try {
      setHinweis(await schritt())
    } catch (e) {
      onFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }
  const kopieren = (welche: string[]) =>
    void tu(async () => {
      const r = await copyCostLines(kalkulationId, positionId, welche)
      return `${r.copied} Zeile(n) in die Ablage kopiert — einfügen in einem anderen Blatt, einer Stückliste oder einem Beleg.`
    })
  const ausAblage = () =>
    void tu(async () => {
      const eintraege = await fetchClipboardArticleIds()
      if (!eintraege.length) return 'Die Ablage hat keine Zeilen, die in ein Kalkulationsblatt passen.'
      const r = await pasteCostLines(kalkulationId, positionId, eintraege)
      onGeaendert()
      return `${r.pasted} Zeile(n) eingefügt.`
    })
  /** ↑ ↓ an den Markierten — der Tastaturweg zum Verschieben (wie in der Stückliste). */
  function schiebe(richtung: -1 | 1) {
    const reihe = richtung < 0 ? [...ids] : [...ids].reverse()
    for (let i = 1; i < reihe.length; i++) {
      if (markiert.has(reihe[i]) && !markiert.has(reihe[i - 1])) [reihe[i - 1], reihe[i]] = [reihe[i], reihe[i - 1]]
    }
    const neu = richtung < 0 ? reihe : reihe.reverse()
    if (neu.some((id, i) => id !== ids[i])) onOrdnen(neu)
  }

  const { amRand } = useRandMarkieren({ behaelter, reihe: ids, markiert, setMarkiert, onKopieren: () => kopieren(auswahl) })

  const summe = zeilen.reduce((s, z) => s + (z.cost ?? 0), 0)
  const gewicht = zeilen.reduce((s, z) => s + z.weight, 0)
  // N6-3: Kosten und Gewicht auch je Einheit der Position.
  const fuerEine = () =>
    zeilen.map((z) =>
      rechneKostenzeile(
        { kind: z.kind, quantity: z.quantity, quantityBasis: z.quantityBasis, wastePercent: z.wastePercent, unitCost: z.unitCost, priceUnit: z.priceUnit, discountPercent: z.discountPercent, weightPerUnit: z.weightPerUnit },
        1,
      ),
    )
  const summeJe = jeEinheitWert(summe, positionsMenge, () => fuerEine().reduce((s, r) => s + r.cost, 0))
  const gewichtJe = jeEinheitWert(gewicht, positionsMenge, () => fuerEine().reduce((s, r) => s + r.weight, 0))
  const gezogeneZeile = zeilen.find((z) => z.id === gezogen)
  const gezogeneIds = new Set(gezogen ? mitGezogen(gezogen) : [])
  const knopf = 'inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted'

  return (
    <section className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Material & Einzelkosten</h3>
        <div className="flex flex-wrap items-center gap-2">
        {auswahl.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1 text-sm" data-auswahl-behalten>
            <span className="mr-1 text-xs text-muted-foreground">{auswahl.length} markiert</span>
            <button type="button" className={knopf} title="In die Ablage kopieren (Strg+C)" onClick={() => kopieren(auswahl)}>
              <Copy className="h-3.5 w-3.5" /> Kopieren
            </button>
            {darf && (
              <>
                <button type="button" className={`${knopf} px-1`} aria-label="nach oben" onClick={() => schiebe(-1)}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" className={`${knopf} px-1`} aria-label="nach unten" onClick={() => schiebe(1)}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        ) : (
          darf && (
            <button type="button" className={knopf} title="Artikelzeilen aus der Ablage ans Ende des Blatts einfügen" onClick={ausAblage}>
              <ClipboardPaste className="h-3.5 w-3.5" /> Aus Ablage
            </button>
          )
        )}
          <KlappUmschalter modus={klappModus} onModus={onKlappModus} />
        </div>
      </div>
      {hinweis && <p className="text-xs text-green-700 dark:text-green-400">{hinweis}</p>}
      {/* N8-6: so breit wie die Liste — hier wird gemessen, welche Anordnung gilt. */}
      <div ref={listenRef}>
      <div ref={behaelter}>
        <ZiehBereich
          dndProps={dndProps}
          vorschau={
            gezogeneZeile && (
              <>
                {gezogeneIds.size > 1 && <span className="mr-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">{gezogeneIds.size} Zeilen</span>}
                <span className="font-medium">{gezogeneZeile.name}</span>
              </>
            )
          }
        >
          <ZeilenListe
            spalten={spalten}
            klappModus={klappModus}
            kopfJeZeile
            fuss={
              <Schlusszeile
                links={`${zeilen.length} Zeile(n)`}
                jeEinheit={jeEinheit}
                werte={[
                  ...(gewicht > 0 ? [{ titel: 'Gewicht', je: gewichtText(gewichtJe), gesamt: gewichtText(gewicht) }] : []),
                  ...(kosten ? [{ titel: 'Kosten', je: geldText(summeJe), gesamt: geldText(summe), fett: true }] : []),
                ]}
              />
            }
          >
            {zeilen.map((z) => (
              <KostenZeile
                key={z.id}
                kalkulationId={kalkulationId}
                positionId={positionId}
                z={z}
                positionsMenge={positionsMenge}
                einheiten={einheiten}
                kategorien={kategorien}
                darf={darf}
                darfDateien={darfDateien}
                kosten={kosten}
                breit={breit}
                markiert={markiert.has(z.id)}
                onRand={amRand(z.id)}
                gezogen={gezogeneIds.has(z.id)}
                ziellinie={ziel?.key === z.id ? ziel.kante : null}
                onGeaendert={onGeaendert}
                onFehler={onFehler}
                onLoeschen={async () => {
                  if (await bestaetige(`Kostenzeile „${z.name}" entfernen?`, { ok: 'Entfernen', gefaehrlich: true })) onLoeschen(z)
                }}
              />
            ))}
            {darf && (
              <KostenZeile
                key={`neu-${neuSchluessel}`}
                kalkulationId={kalkulationId}
                positionId={positionId}
                positionsMenge={positionsMenge}
                einheiten={einheiten}
                kategorien={kategorien}
                vorgabeBezug={vorgabeBezug}
                darf={darf}
                darfDateien={false}
                kosten={kosten}
                breit={breit}
                autoFokus={neuFokus}
                onGeaendert={onGeaendert}
                onFehler={onFehler}
                onAngelegt={onAngelegt}
              />
            )}
            {!darf && zeilen.length === 0 && <p className="px-3 py-4 text-center text-sm text-muted-foreground">Keine Kostenzeilen.</p>}
          </ZeilenListe>
        </ZiehBereich>
      </div>
      </div>
    </section>
  )
}

const NEUE_KOSTENZEILE = {
  kind: 'MATERIAL',
  name: '',
  // KA-49: Die leere Zeile schlägt keine Menge vor — der Platzhalter „1“ zeigt, was leer gilt.
  quantity: null,
  quantityBasis: 'PER_UNIT',
  unitCode: null,
  wastePercent: null,
  unitCost: null,
  priceUnit: 1,
  discountPercent: null,
  supplierId: null,
  weightPerUnit: null,
  altQuantity: null,
  altUnitCode: null,
  description: null,
  articleId: null,
  categoryId: null,
} as unknown as Datensatz

/**
 * Die Tab-Reihenfolge der Materialzeile (Nachtrag 7, N7-4 — ändert N4-4): Menge → Einheit → Bezeichnung →
 * Langtext → Art → Kategorie → Bezug → Verschnitt → EK → PE → Rabatt → Lieferant, dann die nächste Zeile.
 * Die Nummern stehen als `data-haupt` an den Feldern; gesperrte und ausgeblendete überspringt der
 * Zeilenblock. **Alternativmenge, Alternativeinheit und Gewicht stehen nicht in der Kette** — sie
 * bekommen den Fokus nur, wenn man hineinklickt; ebenso die Knöpfe Umrechnen und Dateien.
 */
const KETTE = {
  menge: 1,
  einheit: 2,
  name: 3,
  langtext: 4,
  art: 5,
  kategorie: 6,
  bezug: 7,
  verschnitt: 8,
  ek: 9,
  pe: 10,
  rabatt: 11,
  lieferant: 12,
} as const

function KostenZeile({
  kalkulationId,
  positionId,
  z,
  positionsMenge,
  einheiten,
  kategorien,
  vorgabeBezug = 'PER_UNIT',
  darf,
  darfDateien,
  kosten,
  breit,
  autoFokus,
  markiert,
  onRand,
  gezogen,
  ziellinie,
  onGeaendert,
  onFehler,
  onAngelegt,
  onLoeschen,
}: {
  kalkulationId: string
  positionId: string
  z?: CalcCostLine
  positionsMenge: number
  einheiten: CalcUnit[]
  kategorien: CalcBomCategory[]
  vorgabeBezug?: CalcQuantityBasis
  darf: boolean
  darfDateien: boolean
  kosten: boolean
  /** Nachtrag 8 (N8-6): die breite Anordnung — Kategorie, Verschnitt, PE und Rabatt in der ersten Zeile. */
  breit: boolean
  autoFokus?: boolean
  markiert?: boolean
  /** Druck auf den linken Rand — markieren (N4-2). */
  onRand?: (e: ReactMouseEvent) => void
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  onGeaendert: () => void
  onFehler: (text: string | null) => void
  onAngelegt?: (perTastatur: boolean) => void
  onLoeschen?: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)
  const [sucheName, setSucheName] = useState(false)
  const [sucheLieferant, setSucheLieferant] = useState(false)
  const [dateienOffen, setDateienOffen] = useState(false)
  const [rechnet, setRechnet] = useState(false)
  // Die leere Zeile wird genau einmal angelegt: Der Umrechnen-Knopf legt an, und der Fokus, den er
  // dabei verliert, löste sonst über `onVerlassen` ein zweites Anlegen aus (Befund Browserprobe N3-2).
  const legtAn = useRef(false)
  const url = z ? costLineUrl(kalkulationId, positionId, z.id) : ''
  // N3-2: in kg umgerechnet — die Einheit bleibt kg; Menge in kg und Alternativmenge rechnen einander um (N4-1).
  // N7-3: Umgerechnet ist, was ein Gewicht je Alternativeinheit trägt — sonst sind Alternativmenge und -einheit
  // eine freie Angabe (wie in der Stückliste des Projekts).
  const umgerechnet = Boolean(z && z.altWeightPerUnit != null)

  // Im Lieferantenfeld steht der Name, in der Zeile die Kennung (Muster Stückliste).
  const lieferantenNamen = useRef(new Map<string, string>())
  if (z?.supplier) lieferantenNamen.current.set(z.supplier.id, z.supplier.name)
  const lieferantText = (id: string | null) => (id ? (lieferantenNamen.current.get(id) ?? '') : '')

  const neueZeile = useMemo(() => ({ ...NEUE_KOSTENZEILE, quantityBasis: vorgabeBezug }), [vorgabeBezug])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: z ? `calcCostLine:${z.id}` : `calcCostLine:neu:${positionId}`,
    bezeichnung: z?.name ?? 'neue Kostenzeile',
    daten: (z ?? neueZeile) as unknown as Datensatz,
    schreibe: z
      ? async (patch, stand) => {
          const r = await api.patch<{ data: CalcCostLine }>(url, patch, mitStand(stand))
          onGeaendert()
          return r.data as unknown as Datensatz
        }
      : () => Promise.reject(new Error('Die leere Zeile wird angelegt, nicht geändert.')),
    keepalive: z ? { url, method: 'PATCH' } : undefined,
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: z?.name, gesperrt: !z })
  const d = useStellenDaten(stelle) as unknown as CalcCostLine

  const sperre = !darf
  const art = useFeld<string>(stelle, 'kind', { gruppe, art: 'auswahl', label: 'Art', gesperrt: sperre })
  const name = useFeld<string>(stelle, 'name', { gruppe, label: 'Bezeichnung', pflicht: Boolean(z), gesperrt: sperre })
  const artikel = useFeld<string | null>(stelle, 'articleId', { gruppe, label: 'Artikel', gesperrt: sperre })
  const mengeF = useFeld<number | null>(stelle, 'quantity', { gruppe, art: 'zahl', stellen: 3, label: 'Menge', pflicht: true, gesperrt: sperre })
  const bezug = useFeld<string>(stelle, 'quantityBasis', { gruppe, art: 'auswahl', label: 'Mengenbezug', gesperrt: sperre })
  const einheit = useFeld<string | null>(stelle, 'unitCode', { gruppe, art: 'auswahl', label: 'Einheit', gesperrt: sperre || umgerechnet })
  const altMenge = useFeld<number | null>(stelle, 'altQuantity', { gruppe, art: 'zahl', stellen: 3, label: 'Alternativmenge', pflicht: umgerechnet, gesperrt: sperre })
  const altEinheit = useFeld<string | null>(stelle, 'altUnitCode', { gruppe, art: 'auswahl', label: 'Alternativeinheit', gesperrt: sperre || umgerechnet })
  const altGewicht = useFeld<number | null>(stelle, 'altWeightPerUnit', {
    gruppe,
    art: 'zahl',
    stellen: 4,
    label: `kg je ${z?.altUnitCode ?? 'Einheit'}`,
    gesperrt: sperre || !umgerechnet,
    pruefe: (v) => (!umgerechnet || (v !== null && v > 0) ? null : 'Das Gewicht je Einheit muss größer als 0 sein'),
  })
  const verschnitt = useFeld<number | null>(stelle, 'wastePercent', { gruppe, art: 'zahl', stellen: 2, label: 'Verschnitt %', gesperrt: sperre })
  const ek = useFeld<number | null>(stelle, 'unitCost', { gruppe, art: 'zahl', schreib: geldEingabe, label: 'Einkaufspreis', gesperrt: sperre || !kosten })
  const pe = useFeld<number | null>(stelle, 'priceUnit', {
    gruppe,
    art: 'zahl',
    stellen: 0,
    label: 'Preiseinheit',
    pflicht: true,
    gesperrt: sperre || !kosten,
    pruefe: (v) => (v !== null && v >= 1 ? null : 'Die Preiseinheit ist mindestens 1'),
  })
  const rabatt = useFeld<number | null>(stelle, 'discountPercent', { gruppe, art: 'zahl', stellen: 2, label: 'Rabatt %', gesperrt: sperre || !kosten })
  const beschreibung = useFeld<string | null>(stelle, 'description', { gruppe, label: 'Langtext', gesperrt: sperre })
  const gewicht = useFeld<number | null>(stelle, 'weightPerUnit', { gruppe, art: 'zahl', stellen: 4, label: 'kg je Einheit', gesperrt: sperre || umgerechnet })
  const kategorie = useFeld<string | null>(stelle, 'categoryId', { gruppe, art: 'auswahl', label: 'Kategorie', gesperrt: sperre })

  // Die Kosten der Zeile rechnen mit, sobald Menge, Bezug, Verschnitt, EK, Preiseinheit oder Rabatt getippt sind —
  // nicht erst, wenn die Zeile verlassen und gespeichert ist (Auftraggeber 7.10.2026). Derselbe Rechenweg wie am
  // Server (`calc-core`); die Ergebnisspalte daneben folgt wie bisher mit dem Speichern. Die leere Zeile zeigt
  // ihre Kosten, sobald ein Preis dasteht (leere Menge zählt 1, wie beim Anlegen).
  const kostenWert =
    z && !eingabeOffen(mengeF, bezug, verschnitt, ek, pe, rabatt)
      ? (z.cost ?? 0)
      : !z && ek.wert == null
        ? null
        : rechneKostenzeile(
            {
              kind: (art.wert || 'MATERIAL') as LineCostKind,
              quantity: mengeF.wert ?? (z ? 0 : 1),
              quantityBasis: bezug.wert === 'TOTAL' ? 'TOTAL' : 'PER_UNIT',
              wastePercent: verschnitt.wert,
              unitCost: ek.wert,
              priceUnit: pe.wert,
              discountPercent: rabatt.wert,
            },
            positionsMenge,
          ).cost
  const lieferant = useFeld<string | null>(stelle, 'supplierId', {
    gruppe,
    label: 'Lieferant',
    gesperrt: sperre,
    schreib: lieferantText,
    lies: (text) => {
      const t = text.trim()
      if (t === '') return null
      for (const [id, n] of lieferantenNamen.current) if (n === t) return id
      return (stelle.basis('supplierId') as string | null) ?? null
    },
  })

  const [artikelTreffer, setArtikelTreffer] = useSuche<CalcCostArticle>(name.props.value, sucheName, searchCostArticles)
  const [lieferantTreffer, setLieferantTreffer] = useSuche<{ id: string; name: string }>(lieferant.props.value, sucheLieferant, searchSuppliers)

  function uebernimm(a: CalcCostArticle) {
    setSucheName(false)
    setArtikelTreffer([])
    // Der Server belegt aus dem Artikel vor (Vorzugslieferant, Verschnitt, Gewicht) — Bezeichnung zeigt es sofort.
    name.tippe(a.name)
    artikel.tippe(a.id)
    // N3-1: Einheit und kg je Einheit gleich in der Zeile — nicht erst nach dem Speichern. Steht schon
    // eine andere Einheit da, rechnet der Server das Gewicht in sie um.
    if (!umgerechnet && (!einheit.wert || einheit.wert === a.unitCode)) {
      einheit.tippe(a.unitCode)
      if (a.weightPerUnit != null) gewicht.tippe(zahlText(a.weightPerUnit, 4))
    }
    if (a.preferredSupplier) lieferantenNamen.current.set(a.preferredSupplier.id, a.preferredSupplier.name)
  }

  async function anlegen(perTastatur: boolean, danach?: (neu: CalcCostLine) => Promise<unknown>) {
    const text = (name.wert ?? '').trim()
    if (!onAngelegt || (!text && !artikel.wert) || legtAn.current) return
    legtAn.current = true
    const dto: Record<string, unknown> = {}
    const felder: Record<string, { wert: unknown; gespeichert: unknown }> = {
      kind: art,
      name,
      articleId: artikel,
      quantity: mengeF,
      quantityBasis: bezug,
      unitCode: einheit,
      wastePercent: verschnitt,
      unitCost: ek,
      priceUnit: pe,
      discountPercent: rabatt,
      description: beschreibung,
      weightPerUnit: gewicht,
      altQuantity: altMenge,
      altUnitCode: altEinheit,
      supplierId: lieferant,
      categoryId: kategorie,
    }
    for (const [k, f] of Object.entries(felder)) if (!gleich(f.wert, f.gespeichert) && f.wert !== null && f.wert !== '') dto[k] = f.wert
    if (text) dto.name = text
    try {
      setFehler(null)
      let neu: CalcCostLine
      try {
        neu = await createCostLine(kalkulationId, positionId, dto)
      } catch (e) {
        legtAn.current = false
        throw e
      }
      // Angelegt ist angelegt — schlägt das Umrechnen fehl, steht die Zeile trotzdem im Blatt.
      try {
        if (danach) await danach(neu)
      } finally {
        onAngelegt(perTastatur)
      }
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Kostenzeile ließ sich nicht anlegen.'))
    }
  }

  /**
   * N3-2 — in kg umrechnen bzw. zurück. Was in der Zeile noch nicht gespeichert
   * ist (Menge, Gewicht), geht vorher hinaus; die leere Zeile wird dafür angelegt.
   */
  async function umrechnen(to: 'WEIGHT' | 'ORIGINAL') {
    setFehler(null)
    setRechnet(true)
    try {
      if (!z) {
        await anlegen(false, (neu) => convertCostLine(kalkulationId, positionId, neu.id, to, neu.updatedAt))
        return
      }
      const ergebnis = await gruppe.verlassen()
      if (ergebnis && ergebnis.art !== 'gespeichert') return
      const stand = (stelle.basis('updatedAt') as string | undefined) ?? z.updatedAt
      const neu = await convertCostLine(kalkulationId, positionId, z.id, to, stand)
      stelle.uebernimm(neu as unknown as Datensatz)
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Umrechnen hat nicht geklappt.'))
    } finally {
      setRechnet(false)
    }
  }

  async function dateienAblegen(dateien: File[]) {
    if (!z) return
    setFehler(null)
    const r = await haengeAn(kalkulationId, dateien, { costLineId: z.id })
    if (r.fehler.length) setFehler(r.fehler.join(' · '))
    if (r.ok) {
      setDateienOffen(true)
      onGeaendert()
    }
  }

  const einheitAuswahl = (
    <select className={zelle} data-haupt={KETTE.einheit} {...einheit.props}>
      <option value="">—</option>
      {einheiten.map((u) => (
        <option key={u.id} value={u.code}>
          {u.code}
        </option>
      ))}
    </select>
  )

  // N3-2: Was „in kg" ergäbe — aus den Werten, die gerade in der Zeile stehen (auch ungespeichert).
  const kgJeEinheit = gewicht.wert ?? 0
  const inKg = Math.round((mengeF.wert ?? 1) * kgJeEinheit * 1000) / 1000
  const einheitIstKg = (einheit.wert ?? '').trim().toLowerCase() === 'kg'
  const kannUmrechnen = darf && !umgerechnet && kgJeEinheit > 0 && !einheitIstKg && Boolean(z || (name.wert ?? '').trim() || artikel.wert)
  const knopf = 'inline-flex max-w-full items-center gap-1 rounded border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50'

  const preisstand = (
    <span className="block px-1.5 py-1 text-xs text-muted-foreground">
      {!z
        ? '—'
        : d.priceSource === 'ARTICLE'
          ? `aus dem Artikel${d.priceAgeDays != null ? `, vor ${d.priceAgeDays} Tag(en)` : ''}`
          : d.priceSource === 'OFFER'
            ? 'aus einem Lieferantenangebot'
            : d.unitCost != null
              ? `von Hand${d.priceAgeDays != null ? `, vor ${d.priceAgeDays} Tag(en)` : ''}`
              : 'kein Preis'}
      {z?.articleNumber && ` · Art.-Nr. ${z.articleNumber}`}
      {z && HERKUNFT[z.origin] && ` · ${HERKUNFT[z.origin]}`}
      {z?.request && <AnfrageMarke kalkulationId={kalkulationId} costLineId={z.id} marke={z.request} />}
    </span>
  )

  // Nachtrag 8 (N8-6): Kategorie, Verschnitt, PE und Rabatt stehen je nach Breite der Liste in der ersten oder
  // in der zweiten Zeile — dieselben Felder mit derselben Nummer in der Tab-Kette (N8-9), nur an anderer Stelle.
  const kategorieAuswahl = (
    // N4-3: dieselbe Liste wie in der Stückliste — eine abgeschaltete Kategorie bleibt an ihrer Zeile sichtbar.
    <select className={zelle} data-haupt={KETTE.kategorie} {...kategorie.props}>
      <option value="">—</option>
      {kategorien
        .filter((k) => k.isActive || k.id === kategorie.wert)
        .map((k) => (
          <option key={k.id} value={k.id}>
            {k.name}
          </option>
        ))}
    </select>
  )
  const verschnittEingabe = <input className={`${zelle} text-right`} inputMode="decimal" data-haupt={KETTE.verschnitt} title={breit ? 'Verschnitt in %' : undefined} {...verschnitt.props} />
  const peEingabe = (
    <input className={`${zelle} text-right`} inputMode="numeric" data-haupt={KETTE.pe} title="Preiseinheit — der Preis gilt für so viele Einheiten" {...pe.props} />
  )
  const rabattEingabe = <input className={`${zelle} text-right`} inputMode="decimal" data-haupt={KETTE.rabatt} {...rabatt.props} />

  // N1-34, N6-8, N7-3: die zweite Zeile in Spaltenreihenfolge — **Alternativmenge und -einheit unter Menge und
  // Einheit**, der Langtext unter der Bezeichnung und so breit wie sie. Umgerechnet (N3-2) ist die
  // Alternativmenge die eingegebene Menge: Sie und die Menge in kg rechnen einander um, ihre Einheit steht fest.
  // In beiden Anordnungen gleich.
  const altUndLangtext = (
    <>
      <RasterFeld von="menge" label="Alt.-Menge" schmal="w-24">
        <input
          className={`${zelle} text-right`}
          inputMode="decimal"
          title={
            umgerechnet
              ? `Menge in ${z?.altUnitCode ?? 'der eingegebenen Einheit'} — die Menge in kg folgt daraus`
              : 'Alternativmenge — dieselbe Menge in einer zweiten Einheit, als Angabe neben der Menge'
          }
          {...altMenge.props}
        />
      </RasterFeld>
      <RasterFeld von="einheit" label={null} schmal="w-20">
        <select className={zelle} aria-label="Alternativeinheit" title="Alternativeinheit" {...altEinheit.props}>
          <option value="">—</option>
          {einheiten.map((u) => (
            <option key={u.id} value={u.code}>
              {u.code}
            </option>
          ))}
        </select>
      </RasterFeld>
      <RasterFeld von="name" label="Langtext" schmal="w-full">
        <textarea rows={2} className={zelle} data-haupt={KETTE.langtext} {...beschreibung.props} />
      </RasterFeld>
    </>
  )
  // Das Gewicht je Einheit mit dem Umrechnen in kg (N3-2) und die Dateien der Materialzeile (Detailblatt O 4a) —
  // in beiden Anordnungen unter Menge und Einheit bzw. unter der Bezeichnung (N8-8: alles zum Gewicht steht
  // unter der Alternativmenge).
  const gewichtUndDateien = (
    <>
      <RasterFeld von="menge" bis="einheit" label={umgerechnet ? `kg je ${z?.altUnitCode ?? 'Einheit'}` : 'kg je Einheit'} schmal="w-44">
        {umgerechnet ? (
          <input className={`${zelle} text-right`} inputMode="decimal" {...altGewicht.props} />
        ) : (
          <input className={`${zelle} text-right`} inputMode="decimal" {...gewicht.props} />
        )}
        {!umgerechnet && z && z.weight > 0 && (
          <span className="mt-0.5 block px-1.5 text-right text-xs tabular-nums text-muted-foreground" title="Gewicht dieser Zeile für die ganze Position">
            = {menge(z.weight)} kg
          </span>
        )}
        {umgerechnet ? (
          <button
            type="button"
            className={`${knopf} mt-1`}
            disabled={!darf || rechnet}
            title={`Zurück zur eingegebenen Menge — der Einkaufspreis gilt dann wieder je ${z?.altUnitCode ?? 'Einheit'}`}
            onClick={() => void umrechnen('ORIGINAL')}
          >
            <ArrowLeftRight className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              zurück in {menge(z?.altQuantity)} {z?.altUnitCode ?? ''}
            </span>
          </button>
        ) : (
          kannUmrechnen && (
            <button
              type="button"
              className={`${knopf} mt-1`}
              disabled={rechnet}
              title="Das Gewicht wird zur Menge, der Einkaufspreis zum Kilopreis — die eingegebene Menge bleibt als Alternativmenge stehen"
              onClick={() => void umrechnen('WEIGHT')}
            >
              <ArrowLeftRight className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{menge(inKg)} kg als Menge</span>
            </button>
          )
        )}
      </RasterFeld>
      {z && (
        <RasterFeld von="name" label="Dateien" schmal="w-48">
          <button type="button" className={knopf} aria-expanded={dateienOffen} onClick={() => setDateienOffen((o) => !o)}>
            <Paperclip className="h-3.5 w-3.5 shrink-0" />
            {z.fileCount > 0 ? dateienText(z.fileCount) : darfDateien ? 'hinzufügen' : 'keine'}
          </button>
        </RasterFeld>
      )}
    </>
  )
  const lieferantSuche = (
    <div className="relative">
      <input
        className={zelle}
        data-haupt={KETTE.lieferant}
        {...lieferant.props}
        placeholder={sperre ? '' : 'suchen …'}
        onChange={(e) => {
          lieferant.props.onChange(e)
          setSucheLieferant(true)
        }}
      />
      <Vorschlaege
        allein
        eintraege={lieferantTreffer}
        schluessel={(l) => l.id}
        onWahl={(l) => {
          lieferantenNamen.current.set(l.id, l.name)
          lieferant.setze(l.id)
          setSucheLieferant(false)
          setLieferantTreffer([])
        }}
        darstellung={(l) => l.name}
      />
    </div>
  )

  const zeile2 = breit
    ? [
        // Nachtrag 8 (N8-7): In der breiten Anordnung liegen nur noch Langtext und Lieferant unter der ersten
        // Zeile — der Lieferant unter Art und Kategorie, der Preisstand daneben bis zum rechten Rand. Ohne
        // Kostenrecht fehlen die Preisspalten; der Preisstand (Artikelnummer, Herkunft, Anfrage) reicht dann
        // über das, was rechts vom Lieferanten bleibt.
        <>
          {altUndLangtext}
          <RasterFeld von="art" bis="kategorie" label="Lieferant" schmal="w-56">
            {lieferantSuche}
          </RasterFeld>
          <RasterFeld von="bezug" bis="aktion" label="Preisstand" schmal="w-60">
            {preisstand}
          </RasterFeld>
        </>,
        // N8-8: die dritte Zeile — das Gewicht unter der Alternativmenge, die Dateien unter der Bezeichnung.
        gewichtUndDateien,
      ]
    : [
        // N6-8: Kategorie und Verschnitt unter Art und Bezug, PE und Rabatt unter EK und Kosten.
        <>
          {altUndLangtext}
          <RasterFeld von="art" label="Kategorie" schmal="w-36">
            {kategorieAuswahl}
          </RasterFeld>
          <RasterFeld von="bezug" label="Verschn. %" schmal="w-20">
            {verschnittEingabe}
          </RasterFeld>
          {kosten && (
            <>
              <RasterFeld von="ek" label="PE" schmal="w-16">
                {peEingabe}
              </RasterFeld>
              <RasterFeld von="kosten" label="Rabatt %" schmal="w-20">
                {rabattEingabe}
              </RasterFeld>
            </>
          )}
        </>,
        // Zeile 3: Gewicht und Dateien, der Lieferant unter Art und Bezug (N6-8) und der Preisstand. Sie steht
        // an jeder Zeile, auch an der leeren.
        <>
          {gewichtUndDateien}
          <RasterFeld von="art" bis="bezug" label="Lieferant" schmal="w-56">
            {lieferantSuche}
          </RasterFeld>
          {kosten && (
            <RasterFeld von="ek" bis="aktion" label="Preisstand" schmal="w-60">
              {preisstand}
            </RasterFeld>
          )}
        </>,
        // Ohne Kostenrecht gibt es die Preisspalten nicht — der Preisstand (Artikelnummer, Herkunft, Anfrage) steht dann darunter.
        ...(!kosten && z
          ? [
              <RasterFeld von="name" bis="aktion" label="Preisstand" schmal="w-full">
                {preisstand}
              </RasterFeld>,
            ]
          : []),
      ]

  // Zugeklappt (N6-8): Kategorie und Verschnitt in der zweiten Zeile unter Art und Bezug — dort, wo sie
  // aufgeklappt als Felder stehen. Aus dem gespeicherten Stand der Zeile, nicht aus der offenen Eingabe.
  // Nachtrag 8 (N8-7): In der breiten Anordnung stehen beide schon in der ersten Zeile — dort steht stattdessen
  // der Lieferant unter Art und Kategorie, wo er aufgeklappt als Feld steht.
  const kategorieName = kategorien.find((k) => k.id === d.categoryId)?.name
  const verschnittInfo = d.wastePercent ? `Verschnitt ${menge(d.wastePercent)} %` : null
  const artInfo = breit ? '' : [kategorieName, verschnittInfo].filter(Boolean).join(' · ')
  const lieferantInfo = breit ? lieferantText(d.supplierId ?? null) : ''

  // Zugeklappt: die eingegebene Menge unter Menge und Einheit, die Büroklammer unter der Bezeichnung.
  const zuInfo =
    z && (z.altQuantity !== null || z.fileCount > 0 || (z.request && z.request.state !== 'OPEN') || artInfo || lieferantInfo) ? (
      <>
        {/* N3-10: die Zahl rechtsbündig unter der Menge, die Einheit unter der Einheit — umgerechnet mit „=“ davor. */}
        {z.altQuantity !== null && (
          <>
            <RasterInfo von="menge" rechts>
              {/* 1 px = Rahmen des Mengenfelds darüber: die Ziffern stehen bündig (nachgemessen). */}
              <span
                className="pr-px text-sm tabular-nums text-muted-foreground"
                data-alt-menge
                title={umgerechnet ? `${menge(z.altWeightPerUnit)} kg je ${z.altUnitCode ?? 'Einheit'}` : 'Alternativmenge'}
              >
                {umgerechnet ? '= ' : ''}
                {menge(z.altQuantity)}
              </span>
            </RasterInfo>
            <RasterInfo von="einheit">
              <span className="pl-px text-sm text-muted-foreground">{z.altUnitCode ?? ''}</span>
            </RasterInfo>
          </>
        )}
        {(z.fileCount > 0 || (z.request && z.request.state !== 'OPEN')) && (
          <RasterInfo von="name">
            <span className="text-xs text-muted-foreground">
              {z.fileCount > 0 && (
                <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setDateienOffen((o) => !o)}>
                  <Paperclip className="h-3 w-3" /> {dateienText(z.fileCount)}
                </button>
              )}
              {/* Detailblatt O 10.: die Marke der Anfrage auch zugeklappt. */}
              {z.request && z.request.state !== 'OPEN' && <AnfrageMarke kalkulationId={kalkulationId} costLineId={z.id} marke={z.request} />}
            </span>
          </RasterInfo>
        )}
        {artInfo && (
          <RasterInfo von="art" bis="bezug">
            <span data-art-info title={artInfo}>
              {artInfo}
            </span>
          </RasterInfo>
        )}
        {lieferantInfo && (
          <RasterInfo von="art" bis="kategorie">
            <span data-lieferant-info title={`Lieferant: ${lieferantInfo}`}>
              {lieferantInfo}
            </span>
          </RasterInfo>
        )}
      </>
    ) : undefined

  return (
    <ZeilenBlock
      neu={!z}
      autoFokus={autoFokus}
      schluessel={z?.id}
      gezogen={gezogen}
      ziellinie={ziellinie}
      ziehbar={Boolean(z && darf)}
      markiert={markiert}
      onRand={z ? onRand : undefined}
      randLabel={z?.name}
      tabKette
      gruppe={z ? gruppe : undefined}
      zeilen={zeile2}
      // N7-9: Eine gesperrte Zeile (abgegebene Fassung, ohne Recht) nimmt keinen Fokus — ohne den Pfeil
      // wären Kategorie, Lieferant, Preisstand und Dateien dort nicht mehr zu sehen.
      aufklappbar={!darf}
      zuInfo={zuInfo}
      onDateien={z && darfDateien ? (d) => void dateienAblegen(d) : undefined}
      detail={
        z && dateienOffen ? (
          <KalkulationsDateien kalkulationId={kalkulationId} ziel={{ positionId, costLineId: z.id }} darf={darfDateien} onGeaendert={onGeaendert} kompakt />
        ) : undefined
      }
      onVerlassen={(perTastatur) => {
        setSucheName(false)
        setSucheLieferant(false)
        setArtikelTreffer([])
        setLieferantTreffer([])
        onFehler(null)
        if (!z) void anlegen(perTastatur)
      }}
      onVerwerfen={() => {
        gruppe.verwerfen()
        setFehler(null)
      }}
      fehler={fehler}
    >
      <Feld spalte="menge">
        <input
          data-start
          data-haupt={KETTE.menge}
          className={`${zelle} text-right`}
          inputMode="decimal"
          placeholder={z ? undefined : '1'}
          title={umgerechnet ? `aus ${menge(z?.altQuantity)} ${z?.altUnitCode ?? ''} × ${menge(z?.altWeightPerUnit)} kg — die Alternativmenge folgt der Menge in kg` : undefined}
          {...mengeF.props}
        />
      </Feld>
      <Feld spalte="einheit">{einheitAuswahl}</Feld>
      <Feld spalte="name">
        <div className="relative">
          <input
            className={zelle}
            data-haupt={KETTE.name}
            placeholder={z ? undefined : 'Artikel suchen oder Bezeichnung …'}
            {...name.props}
            onChange={(e) => {
              name.props.onChange(e)
              setSucheName(true)
            }}
          />
          <Vorschlaege
            allein
            eintraege={artikelTreffer}
            schluessel={(a) => a.id}
            onWahl={uebernimm}
            darstellung={(a) => (
              <span className="flex justify-between gap-2">
                <span>{a.name}</span>
                <span className="text-xs text-muted-foreground">
                  {a.unitCode}
                  {a.preferredSupplier ? ` · ${a.preferredSupplier.name}` : ''} · {a.articleNumber}
                </span>
              </span>
            )}
          />
        </div>
      </Feld>
      <Feld spalte="art">
        <select className={zelle} data-haupt={KETTE.art} {...art.props}>
          {KOSTENARTEN.map((k) => (
            <option key={k.wert} value={k.wert}>
              {k.text}
            </option>
          ))}
        </select>
      </Feld>
      {/* Nachtrag 8 (N8-6): In der breiten Anordnung sind Kategorie, Verschnitt, PE und Rabatt Felder der ersten
          Zeile — in Spaltenreihenfolge, denn im Raster sitzt ein Feld in der Spalte, an deren Stelle es steht. */}
      {breit && <Feld spalte="kategorie">{kategorieAuswahl}</Feld>}
      <Feld spalte="bezug">
        <select className={zelle} data-haupt={KETTE.bezug} {...bezug.props}>
          {BEZUG.map((b) => (
            <option key={b.wert} value={b.wert}>
              {b.text}
            </option>
          ))}
        </select>
      </Feld>
      {breit && <Feld spalte="verschnitt">{verschnittEingabe}</Feld>}
      {kosten && (
        <>
          <Feld spalte="ek">
            <GeldFeldFuer
              className={`${zelle} ${d.priceOld ? 'text-amber-700 dark:text-amber-400' : ''}`}
              data-haupt={KETTE.ek}
              title={d.priceOld ? 'Preis älter als in den Einstellungen vorgegeben' : undefined}
              feld={ek.props}
            />
          </Feld>
          {breit && (
            <>
              <Feld spalte="pe">{peEingabe}</Feld>
              <Feld spalte="rabatt">{rabattEingabe}</Feld>
            </>
          )}
          <Feld spalte="kosten">{kostenWert !== null ? nurAnzeige(geldText(kostenWert)) : <span />}</Feld>
        </>
      )}
      <Feld spalte="aktion">
        {z && darf && onLoeschen && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="Kostenzeile entfernen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </Feld>
    </ZeilenBlock>
  )
}

// ── Arbeitsschritte (4.4) ───────────────────────────────────────────────────

function SchrittListe({
  kalkulationId,
  positionId,
  zeilen,
  positionsMenge,
  jeEinheit,
  klappModus,
  onKlappModus,
  herkunft,
  phasen,
  qualis,
  vorlagen,
  saetze,
  verrechnung,
  vorgabeQuali,
  standardsaetze,
  vorgabeBezug,
  positionsEinheit,
  darf,
  kosten,
  neuSchluessel,
  neuFokus,
  onGeaendert,
  onAngelegt,
  onFehler,
  onOrdnen,
  onLoeschen,
}: {
  kalkulationId: string
  positionId: string
  zeilen: CalcWorkStep[]
  /** Menge der Position — Stunden „je Einheit" rechnen damit, während getippt wird. */
  positionsMenge: number
  /** „je lfm“ — Beschriftung der Werte je Einheit in der Schlusszeile. */
  jeEinheit: string
  /** N7-11: immer aufgeklappt, immer zugeklappt oder beim Bearbeiten. */
  klappModus: KlappModus
  onKlappModus: (modus: KlappModus) => void
  /** „K0142-26 · Pos. 3“ — steht an der Ablage, wenn hier Schritte kopiert werden (N7-5). */
  herkunft: string
  phasen: CalcPhase[]
  qualis: CalcQualification[]
  vorlagen: CalcWorkStepTemplate[]
  /** Sätze der Kalkulation im geltenden Lohnmodus (N6-5) — ohne Kostenrecht ohne `rate`. */
  saetze: CalcSheetRate[]
  /** Lohnmodus „Verrechnungssatz“: Der Stundensatz von Hand ist dann ein Verrechnungssatz. */
  verrechnung: boolean
  vorgabeQuali: string | null
  /** Nachtrag 9 (N9-3): Phase → Qualifikation, die sie in dieser Kalkulation vorgibt. */
  standardsaetze?: Record<string, string | null>
  /** Mengenbezug der leeren Zeile (Einstellungen). */
  vorgabeBezug: CalcQuantityBasis
  positionsEinheit: string | null
  darf: boolean
  kosten: boolean
  neuSchluessel: number
  neuFokus: boolean
  onGeaendert: () => void
  onAngelegt: (perTastatur: boolean) => void
  onFehler: (text: string | null) => void
  onOrdnen: (ids: string[]) => void
  onLoeschen: (s: CalcWorkStep) => void
}) {
  // Nachtrag 8 (N8-10): wie die Kostenliste darüber — die Anordnung folgt der eigenen Breite der Liste.
  const { ref: listenRef, breite: listenBreite } = useListenBreite()
  const breit = listenBreite >= SCHRITTE_BREIT
  const spalten = useMemo<Spalte[]>(() => {
    const anordnung: Spalte[] = breit
      ? [
          // Nachtrag 8 (N8-10): Die Beschreibung steht als Feld neben der Tätigkeit, beide teilen sich die
          // freie Breite (5 : 4). Phase und Qualifikation sind breiter — ihre Namen stehen ganz da.
          { key: 'phase', label: 'Phase', spur: '7.5rem', min: 120 },
          { key: 'taetigkeit', label: 'Tätigkeit', spur: 'minmax(10rem,5fr)', min: 160, schmal: 'w-full' },
          { key: 'beschreibung', label: 'Beschreibung', spur: 'minmax(10rem,4fr)', min: 160, schmal: 'w-full' },
          { key: 'quali', label: 'Qualifikation', spur: '8rem', min: 128 },
          { key: 'std', label: 'Stunden', spur: '3.75rem', min: 60, rechts: true },
          { key: 'bezug', label: 'Bezug', spur: '4.5rem', min: 72 },
          { key: 'personen', label: 'Pers.', spur: '2.75rem', min: 44, rechts: true },
          { key: 'gesamt', label: 'Std ges.', spur: '4rem', min: 64, rechts: true },
          { key: 'satz', label: 'Std.-Satz', spur: '4.75rem', min: 76, rechts: true },
          { key: 'lohn', label: 'Lohn', spur: '5.5rem', min: 88, rechts: true },
          { key: 'aktion', label: '', spur: '1.5rem', min: 24 },
        ]
      : [
          // Die festen Spuren sind seit Nachtrag 6 knapper: Mit „Std.-Satz" ist eine Spalte dazugekommen, und
          // das Raster füllt erst die festen Spuren bis zu ihrer Breite, ehe die Tätigkeit wächst — was sie
          // mehr nehmen, fehlt dem einzigen Textfeld der Zeile. Die Summe der `min` ist dieselbe wie vorher
          // (564): Das Raster gilt ab derselben Breite, auch im 1280er Fenster.
          { key: 'phase', label: 'Phase', spur: '5.75rem', min: 72 },
          { key: 'taetigkeit', label: 'Tätigkeit', spur: 'minmax(6rem,3fr)', min: 96, schmal: 'w-full' },
          { key: 'quali', label: 'Qualifikation', spur: '5.75rem', min: 68 },
          { key: 'std', label: 'Stunden', spur: '3.5rem', min: 48, rechts: true },
          { key: 'bezug', label: 'Bezug', spur: '4.5rem', min: 60 },
          { key: 'personen', label: 'Pers.', spur: '2.5rem', min: 36, rechts: true },
          { key: 'gesamt', label: 'Std ges.', spur: '3.5rem', min: 48, rechts: true },
          // Nachtrag 6 (N6-5): der Stundensatz als Einzelpreis des Schritts — der Satz der Qualifikation, überschreibbar.
          { key: 'satz', label: 'Std.-Satz', spur: '4.5rem', min: 52, rechts: true },
          { key: 'lohn', label: 'Lohn', spur: '5rem', min: 60, rechts: true },
          { key: 'aktion', label: '', spur: '1.5rem', min: 24 },
        ]
    return (
      anordnung
        .filter((s) => kosten || !['satz', 'lohn'].includes(s.key))
        // Wie in der Kostenliste: Die festen Spuren dürfen bis zu ihrem `min` schrumpfen — mit der Spalte
        // „Std.-Satz" liefe das Raster sonst zwischen beiden Summen waagrecht über.
        .map((s) => (s.spur.startsWith('minmax') ? s : { ...s, spur: `minmax(${s.min}px,${s.spur})` }))
    )
  }, [kosten, breit])
  // Nachtrag 7 (N7-5) — wie die Kostenzeilen darüber (N4-2): am linken Rand markieren (Strg: dazu, Umschalt:
  // Bereich), markierte wandern gemeinsam, Strg+C oder „Kopieren“ legt sie in die Ablage der Schritte,
  // „Einfügen“ holt sie in dieses oder ein anderes Blatt.
  const { user } = useAuthStore()
  const behaelter = useRef<HTMLDivElement>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [hinweis, setHinweis] = useState<string | null>(null)
  const ids = useMemo(() => zeilen.map((z) => z.id), [zeilen])
  // Ein gelöschter Schritt fällt aus der Markierung.
  useEffect(() => {
    setMarkiert((m) => {
      const n = new Set([...m].filter((id) => ids.includes(id)))
      return n.size === m.size ? m : n
    })
  }, [ids])
  const auswahl = ids.filter((id) => markiert.has(id))
  const mitGezogen = (key: string) => (markiert.has(key) ? auswahl : [key])
  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      if (!darf) return
      const neu = verschiebeMehrereVor(ids, mitGezogen(key), vorKey)
      if (neu !== ids) onOrdnen([...neu])
    },
  })
  /** ↑ ↓ an den Markierten — der Tastaturweg zum Verschieben. */
  function schiebe(richtung: -1 | 1) {
    const reihe = richtung < 0 ? [...ids] : [...ids].reverse()
    for (let i = 1; i < reihe.length; i++) {
      if (markiert.has(reihe[i]) && !markiert.has(reihe[i - 1])) [reihe[i - 1], reihe[i]] = [reihe[i], reihe[i - 1]]
    }
    const neu = richtung < 0 ? reihe : reihe.reverse()
    if (neu.some((id, i) => id !== ids[i])) onOrdnen(neu)
  }

  const [ablage, setAblage] = useState<SchrittAblage | null>(() => leseSchrittAblage(user?.id))
  useEffect(() => beobachteSchrittAblage(() => setAblage(leseSchrittAblage(user?.id))), [user?.id])
  const schritte = (n: number) => `${n} ${n === 1 ? 'Schritt' : 'Schritte'}`
  function kopieren(welche: string[]) {
    if (!user?.id || !welche.length) return
    schreibeSchrittAblage({ userId: user.id, stepIds: welche, herkunft })
    onFehler(null)
    setHinweis(`${schritte(welche.length)} kopiert — in diesem oder einem anderen Blatt einfügen.`)
  }
  /** Vor dem ersten markierten Schritt, sonst am Ende. Die Ablage bleibt — sie lässt sich mehrfach einfügen. */
  async function einfuegen() {
    if (!ablage || !darf) return
    onFehler(null)
    setHinweis(null)
    try {
      const r = await pasteWorkSteps(kalkulationId, positionId, ablage.stepIds, auswahl[0])
      setHinweis(`${schritte(r.pasted)} eingefügt.`)
      onGeaendert()
    } catch (e) {
      onFehler(apiErrorText(e, 'Die Arbeitsschritte ließen sich nicht einfügen.'))
    }
  }
  const { amRand } = useRandMarkieren({ behaelter, reihe: ids, markiert, setMarkiert, onKopieren: () => kopieren(auswahl) })
  const gezogeneIds = new Set(gezogen ? mitGezogen(gezogen) : [])
  const knopf = 'inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted'

  const stunden = zeilen.reduce((s, z) => s + z.hoursTotal, 0)
  const lohn = zeilen.reduce((s, z) => s + (z.laborCost ?? 0), 0)
  // N6-3: Stunden und Lohn auch je Einheit der Position.
  const stundenFuerEine = (z: CalcWorkStep) => rechneArbeitsschritt({ hours: z.hours, quantityBasis: z.quantityBasis, crewSize: z.crewSize }, 1, [], 'COST_PLUS').hours
  const stundenJe = jeEinheitWert(stunden, positionsMenge, () => zeilen.reduce((s, z) => s + stundenFuerEine(z), 0))
  const lohnJe = jeEinheitWert(lohn, positionsMenge, () => zeilen.reduce((s, z) => s + stundenFuerEine(z) * (z.rate ?? 0), 0))
  const gezogeneZeile = zeilen.find((z) => z.id === gezogen)
  const [ausRichtwert, setAusRichtwert] = useState(false)
  const [erfahrung, setErfahrung] = useState(false)
  // Detailblatt N 6.5: ähnliche Positionen — mit Baustein .AI und Kostenrecht.
  const mitErfahrung = useAuthStore().hasModule('CALCULATION.AI') && kosten

  return (
    <section className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Arbeitsschritte</h3>
        <div className="flex flex-wrap items-center gap-3">
          {auswahl.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 text-sm" data-auswahl-behalten>
              <span className="mr-1 text-xs text-muted-foreground">{auswahl.length} markiert</span>
              <button type="button" className={knopf} title="Kopieren (Strg+C) — einfügen in diesem oder einem anderen Blatt" onClick={() => kopieren(auswahl)}>
                <Copy className="h-3.5 w-3.5" /> Kopieren
              </button>
              {darf && (
                <>
                  <button type="button" className={`${knopf} px-1`} aria-label="nach oben" onClick={() => schiebe(-1)}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" className={`${knopf} px-1`} aria-label="nach unten" onClick={() => schiebe(1)}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          )}
          {darf && ablage && (
            <span className="inline-flex items-center overflow-hidden rounded border border-border text-xs" data-auswahl-behalten>
              <button
                type="button"
                className="inline-flex items-center gap-1 px-2 py-1 hover:bg-muted"
                title={`${auswahl.length ? 'Vor dem ersten markierten Schritt einfügen' : 'Am Ende einfügen'} — kopiert aus ${ablage.herkunft}`}
                onClick={() => void einfuegen()}
              >
                <ClipboardPaste className="h-3.5 w-3.5" /> Einfügen ({ablage.stepIds.length})
              </button>
              <button
                type="button"
                className="border-l border-border px-1 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Ablage der Arbeitsschritte leeren"
                title={`kopiert aus ${ablage.herkunft} — Ablage leeren`}
                onClick={() => schreibeSchrittAblage(null)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
          {mitErfahrung && (
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setErfahrung(true)}>
              Erfahrung …
            </button>
          )}
          {darf && (
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setAusRichtwert(true)}>
              aus Richtwert …
            </button>
          )}
          <KlappUmschalter modus={klappModus} onModus={onKlappModus} />
        </div>
      </div>
      {hinweis && <p className="text-xs text-green-700 dark:text-green-400">{hinweis}</p>}
      {erfahrung && <ErfahrungDialog kalkulationId={kalkulationId} positionId={positionId} onClose={() => setErfahrung(false)} />}
      {ausRichtwert && (
        <RichtwertDialog
          kalkulationId={kalkulationId}
          positionId={positionId}
          positionsEinheit={positionsEinheit}
          onClose={() => setAusRichtwert(false)}
          onAngelegt={() => {
            setAusRichtwert(false)
            onAngelegt(false)
          }}
        />
      )}
      {/* N8-10: so breit wie die Liste — hier wird gemessen, welche Anordnung gilt. */}
      <div ref={listenRef}>
      <div ref={behaelter}>
        <ZiehBereich
          dndProps={dndProps}
          vorschau={
            gezogeneZeile && (
              <>
                {gezogeneIds.size > 1 && <span className="mr-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">{gezogeneIds.size} Schritte</span>}
                <span className="font-medium">{gezogeneZeile.activityName}</span>
              </>
            )
          }
        >
          <ZeilenListe
            spalten={spalten}
            klappModus={klappModus}
            fuss={
              <Schlusszeile
                links={`${zeilen.length} Schritt(e)`}
                jeEinheit={jeEinheit}
                werte={[
                  { titel: 'Stunden', je: `${stundenGenau(stundenJe)} h`, gesamt: `${stundenText(stunden)} h` },
                  ...(kosten ? [{ titel: 'Lohn', je: geldText(lohnJe), gesamt: geldText(lohn), fett: true }] : []),
                ]}
              />
            }
          >
            {zeilen.map((s) => (
              <SchrittZeile
                key={s.id}
                kalkulationId={kalkulationId}
                positionId={positionId}
                s={s}
                positionsMenge={positionsMenge}
                phasen={phasen}
                qualis={qualis}
                vorlagen={vorlagen}
                saetze={saetze}
                verrechnung={verrechnung}
                vorgabeQuali={vorgabeQuali}
                standardsaetze={standardsaetze}
                darf={darf}
                kosten={kosten}
                breit={breit}
                markiert={markiert.has(s.id)}
                onRand={amRand(s.id)}
                gezogen={gezogeneIds.has(s.id)}
                ziellinie={ziel?.key === s.id ? ziel.kante : null}
                onGeaendert={onGeaendert}
                onFehler={onFehler}
                onLoeschen={async () => {
                  if (await bestaetige(`Arbeitsschritt „${s.activityName}" entfernen?`, { ok: 'Entfernen', gefaehrlich: true })) onLoeschen(s)
                }}
              />
            ))}
            {darf && (
              <SchrittZeile
                key={`neu-${neuSchluessel}`}
                kalkulationId={kalkulationId}
                positionId={positionId}
                positionsMenge={positionsMenge}
                phasen={phasen}
                qualis={qualis}
                vorlagen={vorlagen}
                saetze={saetze}
                verrechnung={verrechnung}
                vorgabeQuali={vorgabeQuali}
                standardsaetze={standardsaetze}
                vorgabeBezug={vorgabeBezug}
                darf={darf}
                kosten={kosten}
                breit={breit}
                autoFokus={neuFokus}
                onGeaendert={onGeaendert}
                onFehler={onFehler}
                onAngelegt={onAngelegt}
              />
            )}
            {!darf && zeilen.length === 0 && <p className="px-3 py-4 text-center text-sm text-muted-foreground">Keine Arbeitsschritte.</p>}
          </ZeilenListe>
        </ZiehBereich>
      </div>
      </div>
    </section>
  )
}

const NEUER_SCHRITT = {
  phaseKey: null,
  activityName: '',
  qualificationCode: null,
  hours: null,
  quantityBasis: 'PER_UNIT',
  crewSize: null,
  description: null,
  fixedCostRate: null,
  fixedBillingRate: null,
} as unknown as Datensatz

function SchrittZeile({
  kalkulationId,
  positionId,
  s,
  positionsMenge,
  phasen,
  qualis,
  vorlagen,
  saetze,
  verrechnung,
  vorgabeQuali,
  standardsaetze,
  vorgabeBezug = 'PER_UNIT',
  darf,
  kosten,
  breit,
  autoFokus,
  markiert,
  onRand,
  gezogen,
  ziellinie,
  onGeaendert,
  onFehler,
  onAngelegt,
  onLoeschen,
}: {
  kalkulationId: string
  positionId: string
  s?: CalcWorkStep
  positionsMenge: number
  phasen: CalcPhase[]
  qualis: CalcQualification[]
  vorlagen: CalcWorkStepTemplate[]
  saetze: CalcSheetRate[]
  verrechnung: boolean
  vorgabeQuali: string | null
  standardsaetze?: Record<string, string | null>
  vorgabeBezug?: CalcQuantityBasis
  darf: boolean
  kosten: boolean
  /** Nachtrag 8 (N8-10): die breite Anordnung — die Beschreibung als Feld der ersten Zeile. */
  breit: boolean
  autoFokus?: boolean
  markiert?: boolean
  /** Druck auf den linken Rand — markieren (N7-5). */
  onRand?: (e: ReactMouseEvent) => void
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  onGeaendert: () => void
  onFehler: (text: string | null) => void
  onAngelegt?: (perTastatur: boolean) => void
  onLoeschen?: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)
  const [suche, setSuche] = useState(false)
  const url = s ? workStepUrl(kalkulationId, positionId, s.id) : ''
  // Nachtrag 6 (N6-5): Der Stundensatz von Hand hat je Lohnmodus sein eigenes Feld.
  const satzFeld = verrechnung ? 'fixedBillingRate' : 'fixedCostRate'

  const neuerSchritt = useMemo(() => ({ ...NEUER_SCHRITT, quantityBasis: vorgabeBezug }), [vorgabeBezug])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: s ? `calcWorkStep:${s.id}` : `calcWorkStep:neu:${positionId}`,
    bezeichnung: s?.activityName ?? 'neuer Arbeitsschritt',
    daten: (s ?? neuerSchritt) as unknown as Datensatz,
    schreibe: s
      ? async (patch, stand) => {
          const r = await api.patch<{ data: CalcWorkStep }>(url, patch, mitStand(stand))
          onGeaendert()
          return r.data as unknown as Datensatz
        }
      : () => Promise.reject(new Error('Die leere Zeile wird angelegt, nicht geändert.')),
    keepalive: s ? { url, method: 'PATCH' } : undefined,
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: s?.activityName, gesperrt: !s })

  const sperre = !darf
  const quali = useFeld<string | null>(stelle, 'qualificationCode', { gruppe, art: 'auswahl', label: 'Qualifikation', gesperrt: sperre })
  // Nachtrag 6 (N6-4): die Qualifikation, die eine Phase vorgibt — als Kürzel, wie sie am Schritt steht.
  const vorgabeDerPhase = (key: string | null) => {
    // Nachtrag 9 (N9-3): Der Standardsatz der Phase kommt aus der Kalkulation (Reiter „Sätze & Aufschläge");
    // ohne ihn in der Antwort gilt wie vorher die Vorgabe der Einstellungen.
    if (standardsaetze) return key ? (standardsaetze[key] ?? null) : null
    const id = phasen.find((p) => p.key === key)?.defaultQualificationId
    const q = id ? qualis.find((x) => x.id === id) : undefined
    return q?.isActive ? q.code : null
  }
  /**
   * Mit der Phase wechselt die Qualifikation mit — solange sie niemand eigens gewählt hat (leer oder noch die
   * Vorgabe der bisherigen Phase). `null`: Die Qualifikation bleibt, wie sie ist. Der Server hält dieselbe Regel.
   */
  const qualiZurPhase = (bisherigePhase: string | null, neuePhase: string | null) =>
    !quali.wert || quali.wert === vorgabeDerPhase(bisherigePhase) ? { qualificationCode: vorgabeDerPhase(neuePhase) } : null
  const phase = useFeld<string | null>(stelle, 'phaseKey', {
    gruppe,
    art: 'auswahl',
    label: 'Phase',
    gesperrt: sperre,
    // Als Folgefeld: Phase und Qualifikation gehen im selben PATCH hinaus.
    folge: (neu) => qualiZurPhase((stelle.basis('phaseKey') as string | null) ?? null, neu) ?? {},
  })
  const taetigkeit = useFeld<string>(stelle, 'activityName', { gruppe, label: 'Tätigkeit', pflicht: Boolean(s), gesperrt: sperre })
  const std = useFeld<number | null>(stelle, 'hours', { gruppe, art: 'zahl', stellen: 3, label: 'Stunden', gesperrt: sperre })
  const bezug = useFeld<string>(stelle, 'quantityBasis', { gruppe, art: 'auswahl', label: 'Mengenbezug', gesperrt: sperre })
  const personen = useFeld<number | null>(stelle, 'crewSize', {
    gruppe,
    art: 'zahl',
    stellen: 0,
    label: 'Personen',
    pflicht: true,
    gesperrt: sperre,
    // KA-49: In der leeren Zeile heißt leer „eine Person“ (Platzhalter), sonst mindestens eine.
    pruefe: (v) => ((v === null && !s) || (v !== null && v >= 1 && Number.isInteger(v)) ? null : 'Mindestens eine Person'),
  })
  const beschreibung = useFeld<string | null>(stelle, 'description', { gruppe, label: 'Beschreibung', gesperrt: sperre })
  const satz = useFeld<number | null>(stelle, satzFeld, { gruppe, art: 'zahl', schreib: geldEingabe, label: 'Stundensatz', gesperrt: sperre || !kosten })

  // N6-5: Der Satz der Qualifikation, wie sie gerade dasteht — ohne eigene die Vorgabe der Kalkulation
  // (`satzFuer` im Rechenkern). Er steht im Feld „Std.-Satz", solange dort keiner von Hand gesetzt ist.
  const satzDerQuali = (quali.wert ? saetze.find((x) => x.qualificationCode === quali.wert) : saetze.find((x) => x.isDefault))?.rate ?? null
  const satzGilt = satz.wert ?? satzDerQuali

  // Stunden gesamt und Lohn rechnen mit, sobald Stunden, Bezug, Personen, Qualifikation oder Satz geändert
  // sind — wie die Kosten einer Materialzeile (Auftraggeber 7.10.2026), mit dem Rechenweg des Servers.
  const tippt = !s || eingabeOffen(std, bezug, personen, quali, satz)
  const stundenGesamt = !tippt
    ? s.hoursTotal
    : !s && std.wert == null
      ? null
      : rechneArbeitsschritt({ hours: std.wert ?? 0, quantityBasis: bezug.wert === 'TOTAL' ? 'TOTAL' : 'PER_UNIT', crewSize: personen.wert }, positionsMenge, [], 'COST_PLUS').hours
  const lohn = !tippt ? (s.laborCost ?? 0) : stundenGesamt !== null && satzGilt !== null ? stundenGesamt * satzGilt : null
  const satzFehlt = !tippt ? s.rateMissing : satzGilt === null && (stundenGesamt ?? 0) > 0

  // Vorlagen als Schnellauswahl: je Phase, gefiltert nach der Eingabe (AP-35 Blatt 13).
  const phaseVon = useMemo(() => new Map(phasen.map((p) => [p.id, p])), [phasen])
  const q = taetigkeit.props.value.trim().toLowerCase()
  const treffer = suche && !s ? vorlagen.filter((v) => !q || v.activityName.toLowerCase().includes(q)).slice(0, 12) : []

  async function anlegen(perTastatur: boolean, dto?: Record<string, unknown>) {
    const text = (taetigkeit.wert ?? '').trim()
    if (!onAngelegt || (!text && !dto)) return
    const daten: Record<string, unknown> = dto ?? {
      activityName: text,
      ...(phase.wert ? { phaseKey: phase.wert } : {}),
      ...(quali.wert ? { qualificationCode: quali.wert } : {}),
      ...(std.wert != null ? { hours: std.wert } : {}),
      quantityBasis: bezug.wert ?? 'PER_UNIT',
      crewSize: personen.wert ?? 1,
      ...(beschreibung.wert ? { description: beschreibung.wert } : {}),
      ...(satz.wert != null ? { [satzFeld]: satz.wert } : {}),
    }
    try {
      setFehler(null)
      await createWorkStep(kalkulationId, positionId, daten)
      onAngelegt(perTastatur)
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Arbeitsschritt ließ sich nicht anlegen.'))
    }
  }

  const quellQuali = s?.qualificationCode ?? null
  return (
    <ZeilenBlock
      neu={!s}
      autoFokus={autoFokus}
      schluessel={s?.id}
      gezogen={gezogen}
      ziellinie={ziellinie}
      ziehbar={Boolean(s && darf)}
      markiert={markiert}
      onRand={s ? onRand : undefined}
      randLabel={s?.activityName}
      gruppe={s ? gruppe : undefined}
      // N7-9: wie an der Materialzeile — gesperrt nur über den Pfeil aufzuklappen.
      aufklappbar={!darf}
      zeilen={[
        // Nachtrag 8 (N8-10): In der breiten Anordnung steht die Beschreibung in der ersten Zeile —
        // aufzuklappen bleibt nur der Rechenweg.
        ...(breit
          ? []
          : [
              <RasterFeld key="b" von="taetigkeit" bis={kosten ? 'lohn' : 'gesamt'} label="Beschreibung" schmal="w-full">
                <textarea rows={2} className={zelle} {...beschreibung.props} />
              </RasterFeld>,
            ]),
        ...(s?.derivation
          ? [
              <RasterFeld key="w" von="taetigkeit" bis={kosten ? 'lohn' : 'gesamt'} label="Rechenweg" schmal="w-full">
                <RechenwegZeile kalkulationId={kalkulationId} positionId={positionId} schritt={s} darf={darf} onGeaendert={onGeaendert} />
              </RasterFeld>,
            ]
          : []),
      ]}
      onVerlassen={(perTastatur) => {
        setSuche(false)
        onFehler(null)
        if (!s) void anlegen(perTastatur)
      }}
      onVerwerfen={() => {
        gruppe.verwerfen()
        setFehler(null)
      }}
      fehler={fehler}
    >
      <Feld spalte="phase">
        <select
          className={zelle}
          {...phase.props}
          onChange={(e) => {
            // N6-4: In der leeren Zeile geht nichts hinaus — dort stellt die Wahl der Phase die Qualifikation
            // gleich mit um. An einer bestehenden Zeile erledigt das Folgefeld der Phase dasselbe beim Speichern.
            const folge = s ? null : qualiZurPhase(phase.wert, e.target.value || null)
            phase.props.onChange(e)
            if (folge) quali.tippe(folge.qualificationCode ?? '')
          }}
        >
          <option value="">—</option>
          {phasen.map((p) => (
            <option key={p.id} value={p.key}>
              {p.name}
            </option>
          ))}
        </select>
      </Feld>
      <Feld spalte="taetigkeit">
        <div className="relative">
          <input
            className={zelle}
            data-start
            placeholder={s ? undefined : 'Tätigkeit oder Vorlage …'}
            {...taetigkeit.props}
            onChange={(e) => {
              taetigkeit.props.onChange(e)
              setSuche(true)
            }}
            onFocus={() => !s && setSuche(true)}
          />
          <Vorschlaege
            // Die Wahl legt den Schritt selbst an — Tab und Enter enden hier, sonst legte das Verlassen der Zeile
            // einen zweiten aus dem getippten Text an. Mit der Tastatur gewählt, bekommt die nächste leere Zeile den Fokus.
            weiter={false}
            eintraege={treffer}
            schluessel={(v) => v.id}
            onWahl={(v, perTastatur) => {
              setSuche(false)
              void anlegen(perTastatur, { templateId: v.id })
            }}
            darstellung={(v) => (
              <span className="flex justify-between gap-2">
                <span>{v.activityName}</span>
                <span className="text-xs text-muted-foreground">
                  {phaseVon.get(v.phaseId)?.name ?? ''}
                  {v.plannedHours != null ? ` · ${zahlText(Number(v.plannedHours), 2)} h ${v.quantityBasis === 'PER_UNIT' ? 'je E' : 'gesamt'}` : ''}
                </span>
              </span>
            )}
          />
        </div>
      </Feld>
      {breit && (
        <Feld spalte="beschreibung">
          {/* Nachtrag 8 (N8-10): einzeilig wie die Felder daneben; beim Bearbeiten klappt sie **über** die Zeile
              auf, ohne sie zu verschieben — die Zelle behält ihre Höhe (ein Feld: Zeile 1,25rem + Innenabstand
              0,5rem + Rahmen), nur die Textarea darin wächst. Zugeklappt ohne Umbruch: Was nicht passt, ist
              abgeschnitten statt die Zeile höher zu machen. */}
          <div className="relative h-[calc(1.75rem+2px)]">
            <textarea
              rows={1}
              className={`${zelle} absolute inset-x-0 top-0 h-full resize-none overflow-hidden whitespace-nowrap focus:z-10 focus:h-[4.6rem] focus:overflow-auto focus:whitespace-pre-wrap focus:shadow-md`}
              {...beschreibung.props}
            />
          </div>
        </Feld>
      )}
      <Feld spalte="quali">
        <select className={zelle} {...quali.props} title={!quellQuali && vorgabeQuali ? `ohne Angabe: ${vorgabeQuali} (Vorgabe der Kalkulation)` : undefined}>
          <option value="">{vorgabeQuali ? `Vorgabe (${vorgabeQuali})` : '—'}</option>
          {qualis.map((x) => (
            <option key={x.id} value={x.code}>
              {x.name}
            </option>
          ))}
        </select>
      </Feld>
      <Feld spalte="std">
        <input className={`${zelle} text-right`} inputMode="decimal" title={s?.derivation ? `Rechenweg: ${rechenwegText(s.derivation, 'h')}` : undefined} {...std.props} />
      </Feld>
      <Feld spalte="bezug">
        <select className={zelle} {...bezug.props}>
          {BEZUG.map((b) => (
            <option key={b.wert} value={b.wert}>
              {b.text}
            </option>
          ))}
        </select>
      </Feld>
      <Feld spalte="personen">
        <input className={`${zelle} text-right`} inputMode="numeric" placeholder={s ? undefined : '1'} {...personen.props} />
      </Feld>
      <Feld spalte="gesamt">{stundenGesamt !== null ? nurAnzeige(stundenText(stundenGesamt)) : <span />}</Feld>
      {kosten && (
        <>
          <Feld spalte="satz">
            {/* N6-5: wie der EP von Hand — leer steht der Satz der Qualifikation da, ein eigener ist hervorgehoben. */}
            <GeldFeldFuer
              className={`${zelle} ${satz.wert == null ? 'placeholder:text-foreground' : 'font-medium text-sky-800 dark:text-sky-300'}`}
              data-satz
              placeholder={satzDerQuali !== null ? geldText(satzDerQuali) : ''}
              title={
                satz.wert != null
                  ? `Stundensatz von Hand — Satz der Qualifikation: ${geldText(satzDerQuali) || 'keiner'}. Leeren stellt ihn wieder her.`
                  : `${verrechnung ? 'Verrechnungssatz' : 'Kostensatz'} der Qualifikation — tippen setzt den Stundensatz für diesen Schritt von Hand`
              }
              feld={satz.props}
            />
          </Feld>
          <Feld spalte="lohn">
            {satzFehlt ? (
              <span className="block px-1.5 py-1 text-right text-xs text-destructive" title="Für diese Qualifikation fehlt der Satz — Sätze & Aufschläge, oder den Stundensatz von Hand eintragen">
                Satz fehlt
              </span>
            ) : lohn !== null ? (
              nurAnzeige(geldText(lohn))
            ) : (
              <span />
            )}
          </Feld>
        </>
      )}
      <Feld spalte="aktion">
        {s && darf && onLoeschen && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="Arbeitsschritt entfernen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </Feld>
    </ZeilenBlock>
  )
}

// ── Ergebnisspalte (9.) ─────────────────────────────────────────────────────

function Ergebnisspalte({
  kalkulationId,
  blatt,
  stelle,
  phasen,
  jeEinheit,
  darf,
  kosten,
  daneben,
  breit,
  onGeaendert,
}: {
  kalkulationId: string
  blatt: CalcSheet
  stelle: Speicherstelle<unknown>
  /** Für die Namen der Phasen in der Stundenaufstellung (N6-7). */
  phasen: CalcPhase[]
  /** „je lfm“ — Überschrift der Spalte je Einheit (N6-6). */
  jeEinheit: string
  darf: boolean
  kosten: boolean
  daneben: boolean
  /** Neben den Rastern ist Platz für die breitere Spalte. */
  breit: boolean
  onGeaendert: () => void
}) {
  const p = blatt.position
  const ep = useFeld<number | null>(stelle, 'fixedUnitPrice', { art: 'zahl', schreib: geldEingabe, label: 'EP von Hand', gesperrt: !darf })

  // Abweichende Aufschläge: eine Speicherstelle je Blatt, ein Feld je Aufschlag — Stand ist der der Position.
  const pid = p.id
  const aufschlagDaten = useMemo(
    () => ({ ...Object.fromEntries(blatt.surcharges.map((s) => [s.id, s.override ?? null])), updatedAt: p.updatedAt }) as unknown as Datensatz,
    [blatt.surcharges, p.updatedAt],
  )
  const alsAufschlaege = (b: CalcSheet) => ({ ...Object.fromEntries(b.surcharges.map((x) => [x.id, x.override ?? null])), updatedAt: b.position.updatedAt }) as unknown as Datensatz
  const aufschlaege = useSpeicherstelle<Datensatz>({
    schluessel: `calcSurcharges:${pid}`,
    bezeichnung: 'Aufschläge der Position',
    daten: aufschlagDaten,
    // N1-35: Ein 409 bringt das ganze Blatt (`{ data: CalcSheet }`) — Werte und Stand stehen dort
    // in `surcharges` und `position.updatedAt`. Ohne diese Übersetzung fand die Stelle keinen neuen
    // Stand, schickte viermal den alten und gab auf (rote Felder, nichts gespeichert).
    werte: (a) => {
      const blattIn = (a as unknown as { data?: CalcSheet })?.data
      return blattIn?.surcharges && blattIn.position ? alsAufschlaege(blattIn) : a
    },
    schreibe: async (patch, stand) => {
      let s = stand
      let letzte: CalcSheet | null = null
      for (const [sid, wert] of Object.entries(patch)) {
        const r = await api.patch<{ data: CalcSheet }>(surchargeUrl(kalkulationId, pid, sid), { percent: wert }, mitStand(s))
        letzte = r.data.data
        s = letzte.position.updatedAt
      }
      onGeaendert()
      return letzte ? alsAufschlaege(letzte) : aufschlagDaten
    },
  }) as Speicherstelle<unknown>

  const M = p.quantity
  const je = p.perUnit ?? null
  const einzel = (p.costLabor ?? 0) + (p.costMaterial ?? 0) + (p.costSubcontract ?? 0) + (p.costEquipment ?? 0) + (p.costOther ?? 0)
  const einzelJe = je ? (je.costLabor ?? 0) + (je.costMaterial ?? 0) + (je.costSubcontract ?? 0) + (je.costEquipment ?? 0) + (je.costOther ?? 0) : null
  const zaehlt = !['OPTIONAL', 'ALTERNATIVE', 'TEXT'].includes(p.kind)
  const db = p.totalPrice - einzel
  // Je Einheit ist der DB der EP ohne die Einzelkosten je Einheit — das gilt auch ohne Menge (Aufzahlung).
  const dbJe = einzelJe === null ? null : p.unitPrice - einzelJe
  const geld = (n: number | null | undefined) => (n == null ? '—' : geldText(n))
  const std = (h: number | null | undefined) => (h == null ? '—' : `${stundenGenau(h)} h`)

  // N6-7: Stunden je Phase mit dem **Namen** der Phase, in der Reihenfolge der Phasenliste — der Schlüssel
  // („ASSEMBLY") ist eine Kennung, keine Beschriftung. Was keiner Phase der Liste gehört, steht zuletzt.
  const jePhase = p.hoursByPhase ?? {}
  const phasenZeilen = [
    ...phasen.filter((ph) => ph.key in jePhase).map((ph) => ({ key: ph.key, name: ph.name })),
    ...Object.keys(jePhase)
      .filter((k) => !phasen.some((ph) => ph.key === k))
      .map((k) => ({ key: k, name: k || 'ohne Phase' })),
  ]

  /**
   * N6-6: Eine Zeile der Ergebnistabelle — Beschriftung, Wert **je Einheit** der Position, Wert **gesamt**.
   * Die drei Zellen liegen im Raster des Kastens (`contents`), damit die Zahlen aller Abschnitte untereinander stehen.
   */
  const zeile = (label: string, jeWert: string, gesamt: string, opt: { fett?: boolean; klein?: boolean; gross?: boolean; strich?: boolean; titel?: string } = {}) => {
    const rand = opt.strich ? 'mt-1 border-t border-border pt-1' : ''
    // `gross` (N7-2): die Zahlen eine Schriftgröße größer — die Beschriftung bleibt, wird aber so kräftig wie der Wert.
    const wert = `text-right tabular-nums ${opt.gross ? 'text-base leading-6' : ''} ${rand}`
    return (
      <div role="row" className={`contents ${opt.fett ? 'font-semibold' : ''} ${opt.klein ? 'text-xs' : ''}`} data-gross={opt.gross || undefined}>
        <span
          role="rowheader"
          className={`min-w-0 truncate ${opt.gross ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'} ${opt.klein ? 'pl-3' : ''} ${rand}`}
          title={opt.titel ?? label}
        >
          {label}
        </span>
        <span role="cell" className={wert}>
          {jeWert}
        </span>
        <span role="cell" className={wert}>
          {gesamt}
        </span>
      </div>
    )
  }
  const trenner = <div className="col-span-3 my-1.5 border-t border-border" />

  return (
    // N1-14: Überschrift über dem Kasten wie bei „Material & Einzelkosten“ — beide Kästen beginnen auf einer Höhe.
    // Unter den Rastern (schmales Blatt) bleibt der Kasten schmal: Über die ganze Breite stünden Beschriftung und
    // Zahlen einen halben Bildschirm auseinander.
    <aside className={`shrink-0 space-y-1.5 ${daneben ? `sticky top-4 ${breit ? 'w-80' : 'w-72'}` : 'w-full max-w-md'}`}>
      <h3 className="text-sm font-semibold">Ergebnis</h3>
      <div className="rounded-lg border border-border bg-card p-4">
        <div role="table" aria-label="Ergebnis der Position" className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-3 gap-y-0.5 text-sm" data-ergebnis>
          <div role="row" className="contents text-xs text-muted-foreground">
            <span />
            <span role="columnheader" className="text-right">
              {jeEinheit}
            </span>
            <span role="columnheader" className="text-right">
              gesamt
            </span>
          </div>
          {kosten && (
            <>
              {zeile('Lohn', geld(je?.costLabor), geldText(p.costLabor))}
              {zeile('Material', geld(je?.costMaterial), geldText(p.costMaterial))}
              {zeile('Fremdleistung', geld(je?.costSubcontract), geldText(p.costSubcontract))}
              {zeile('Gerät', geld(je?.costEquipment), geldText(p.costEquipment))}
              {zeile('Sonstiges', geld(je?.costOther), geldText(p.costOther))}
              {zeile('Einzelkosten', geld(einzelJe), geldText(einzel), { fett: true, strich: true })}
            </>
          )}
          {kosten && blatt.surcharges.length > 0 && (
            <>
              {trenner}
              <div className="col-span-3 text-xs font-medium text-muted-foreground">Aufschläge {darf && '— abweichend nur für diese Position'}</div>
              {blatt.surcharges.map((s) => (
                <AufschlagZeile key={s.id} s={s} stelle={aufschlaege} darf={darf} />
              ))}
            </>
          )}
          {trenner}
          {kosten && zeile('Preis kalkuliert', geld(je?.priceCalculated), geldText(p.priceCalculated), { titel: 'Kosten samt Aufschlägen — vor der Rundung und ohne EP von Hand' })}
          <label className="contents">
            <span className="self-center text-muted-foreground">EP von Hand</span>
            <span className="col-span-2 justify-self-end">
              <GeldFeldFuer className={`${zelle} !w-28 ${p.fixedUnitPrice != null ? 'font-medium text-sky-800 dark:text-sky-300' : ''}`} placeholder="kalkuliert" feld={ep.props} />
            </span>
          </label>
          {p.fixedUnitPrice != null && p.manualDeviation != null && (
            <div role="row" className="contents">
              <span role="rowheader" className="text-muted-foreground">
                Abweichung
              </span>
              <span role="cell" className="col-span-2 text-right tabular-nums">
                {geldText(p.manualDeviation)}
                {p.manualDeviationPercent != null ? ` (${zahl(p.manualDeviationPercent, 1)} %)` : ''}
              </span>
            </div>
          )}
          {zeile('Preis (EP · GP)', geldText(p.unitPrice), geldText(p.totalPrice), { fett: true, gross: true, titel: 'Einheitspreis (EP) und Gesamtpreis (GP = EP × Menge)' })}
          {zeile('davon Lohn', geldText(p.unitPriceLabor), geldText(p.unitPriceLabor * M), { klein: true })}
          {zeile('davon Sonstiges', geldText(p.unitPriceOther), geldText(p.unitPriceOther * M), { klein: true })}
          {!zaehlt && <p className="col-span-3 text-xs text-muted-foreground">Zählt nicht zur Angebotssumme.</p>}
          {trenner}
          {zeile('Stunden', std(je?.hours), `${stundenText(p.hours)} h`)}
          {phasenZeilen.length > 1 && phasenZeilen.map((ph) => <Fragment key={ph.key}>{zeile(ph.name, std(je?.hoursByPhase[ph.key]), `${stundenText(jePhase[ph.key])} h`, { klein: true })}</Fragment>)}
          {zeile('Gewicht', je ? gewichtText(je.weight) : '—', gewichtText(p.weight))}
          {kosten && zaehlt && zeile('DB', geld(dbJe), geldText(db), { titel: 'Deckungsbeitrag — der Preis ohne die Einzelkosten' })}
          {kosten && zaehlt && p.totalPrice !== 0 && zeile('DB in % vom Preis', '', `${zahl((db / p.totalPrice) * 100, 1)} %`, { klein: true })}
          {p.rateMissing && <p className="col-span-3 text-xs text-destructive">Ein Arbeitsschritt hat keinen Satz — Lohn dafür 0.</p>}
        </div>
      </div>
    </aside>
  )
}

/** N6-6: worauf ein Aufschlag wirkt — „auf Lohn“, „auf alle Kosten außer Lohn“, „auf alle Kosten, laufend“. */
function aufschlagAuf(s: CalcSheet['surcharges'][number]): string {
  const auf = ALLE_KOSTENARTEN.filter((k) => s.appliesTo.includes(k))
  const worauf =
    auf.length === ALLE_KOSTENARTEN.length
      ? 'alle Kosten'
      : auf.length === ALLE_KOSTENARTEN.length - 1 && !auf.includes('LABOR')
        ? 'alle Kosten außer Lohn'
        : auf.map((k) => KOSTENART_TEXT[k]).join(', ') || '—'
  return `auf ${worauf}${s.basis === 'RUNNING' ? ', laufend' : ''}`
}

/**
 * Ein Aufschlag der Ergebnistabelle (N6-6): oben der **ganze Name** und worauf er wirkt — zwei Aufschläge,
 * die beide mit „Gemeinkosten“ beginnen, waren abgeschnitten nicht zu unterscheiden. Darunter der Satz
 * (abweichend nur für diese Position) und die Wirkung je Einheit und gesamt.
 */
function AufschlagZeile({ s, stelle, darf }: { s: CalcSheet['surcharges'][number]; stelle: Speicherstelle<unknown>; darf: boolean }) {
  const f = useFeld<number | null>(stelle, s.id, { art: 'zahl', stellen: 2, label: s.name, gesperrt: !darf })
  return (
    <>
      <div
        className="col-span-3 mt-1 min-w-0 break-words leading-snug"
        title={s.basis === 'RUNNING' ? 'laufend — auf die Kosten samt den vorigen Aufschlägen' : 'direkt — auf die Einzelkosten'}
        data-aufschlag
      >
        {s.name} <span className="text-xs text-muted-foreground">· {aufschlagAuf(s)}</span>
      </div>
      <span className="flex items-baseline justify-end gap-1">
        <input
          className={`${zelle} !w-16 text-right ${s.override != null ? 'font-medium text-sky-800 dark:text-sky-300' : ''}`}
          inputMode="decimal"
          placeholder={zahl(s.percent ?? 0)}
          aria-label={`${s.name} in Prozent`}
          {...f.props}
        />
        <span className="text-xs text-muted-foreground">%</span>
      </span>
      <span className="text-right tabular-nums">{s.amountPerUnit == null ? '—' : geldText(s.amountPerUnit)}</span>
      <span className="text-right tabular-nums">{geldText(s.amount ?? 0)}</span>
    </>
  )
}

/**
 * Detailblatt O 10. — die Marke der Materialanfrage beim Preisstand: „angefragt bei
 * Stahl Maier · Frist 2.10." · „2 Angebote · bestes 17,90 (Stahl Maier)" · „aus Angebot
 * PA-26-0012" · „nicht anfragen". Ein Klick öffnet das Cockpit, gefiltert auf die Kalkulation.
 *
 * AP-39 Nachtrag 2 (6.1): Liegt ein Angebot vor, wird die Marke zum Vorschlag — vorn „845,00 übernehmen" (die
 * Vorschau für das beste Angebot dieser Zeile), dahinter die Angebote, ein Klick darauf öffnet alle der Kalkulation
 * („Angebote übernehmen"). Der Knopf steht **vorn**, weil die Marke zugeklappt unter der Bezeichnung steht und dort
 * abgeschnitten wird — kürzer wird der Text, nie die Handlung. Was die KI gelesen hat und noch niemand bestätigt hat,
 * führt zur Preisanfrage (PE-122); was kein Lieferant anbietet, sagt die Marke statt „angefragt bei".
 */
function AnfrageMarke({ kalkulationId, costLineId, marke }: { kalkulationId: string; costLineId: string; marke: CalcRequestMark }) {
  const angebote = useContext(AngebotsKontext)
  const tag = (iso: string | null) => (iso ? `${Number(iso.slice(8, 10))}.${Number(iso.slice(5, 7))}.` : '')
  if (marke.state === 'ANSWERED') {
    const uebernehmbar = !!angebote?.uebernehmen && !!marke.bestRequestLineId && marke.bestPrice != null
    const anzahl = `${marke.count} Angebot${marke.count === 1 ? '' : 'e'}`
    // Mit dem Knopf vorn steht der Preis schon dort — dahinter nur noch, von wem.
    const text = uebernehmbar
      ? `${anzahl}${marke.supplierName ? `, bestes von ${marke.supplierName}` : ''}`
      : `${anzahl}${marke.bestPrice != null ? ` · bestes ${geldText(marke.bestPrice)}` : ''}${marke.supplierName ? ` (${marke.supplierName})` : ''}`
    return (
      <>
        {' · '}
        <span className="rounded bg-emerald-100 px-1 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" data-angebot-marke title={`${anzahl}${marke.bestPrice != null ? ` · bestes ${geldText(marke.bestPrice)}` : ''}${marke.supplierName ? ` (${marke.supplierName})` : ''}`}>
          {uebernehmbar && (
            <>
              <button
                type="button"
                className="font-semibold hover:underline"
                title={`Das beste Angebot${marke.supplierName ? ` von ${marke.supplierName}` : ''} in diese Zeile übernehmen — zuerst die Vorschau${
                  marke.bestOfferUnitCode ? ` (die Zeile wird auf ${marke.bestOfferUnitCode} umgestellt, ${geldText(marke.bestPrice)} je Einheit der Zeile)` : ''
                }`}
                onClick={() => angebote!.uebernehmen!(costLineId, marke.bestRequestLineId!)}
              >
                {/* So, wie es übernommen wird: ein Kilopreis bleibt ein Kilopreis (O-57). */}
                {marke.bestOfferUnitCode && marke.bestOfferPrice != null ? `${preisText(marke.bestOfferPrice)} je ${marke.bestOfferUnitCode}` : preisText(marke.bestPrice)} übernehmen
              </button>
              {' · '}
            </>
          )}
          {angebote?.vergleichen ? (
            <button type="button" className="hover:underline" title="Alle Angebote dieser Kalkulation vergleichen und übernehmen" onClick={angebote.vergleichen}>
              {text}
            </button>
          ) : (
            text
          )}
        </span>
      </>
    )
  }
  // Kein Lieferant bietet sie an, und keiner ist mehr offen — „angefragt bei" wäre hier falsch.
  if (marke.state === 'REQUESTED' && !marke.readCount && marke.notOfferedBy?.length && !marke.openCount) {
    return (
      <>
        {' · '}
        <Link
          to={`/calculations/material-requests?tab=anfragen&calculationId=${kalkulationId}`}
          className="rounded bg-amber-100 px-1 text-amber-900 hover:underline dark:bg-amber-900/40 dark:text-amber-100"
          title="Der Lieferant hat diese Zeile ausdrücklich nicht angeboten — anderswo anfragen oder den Preis von Hand setzen"
        >
          nicht angeboten ({marke.notOfferedBy.join(', ')})
        </Link>
      </>
    )
  }
  if (marke.state === 'REQUESTED' && marke.readCount && marke.documentId) {
    return (
      <>
        {' · '}
        <Link
          to={`/correspondence/${marke.documentId}`}
          className="rounded bg-violet-500/15 px-1 text-violet-700 hover:underline dark:text-violet-300"
          title="Die KI hat das Angebot gelesen — erst „Bestätigen“ an der Preisanfrage macht es zur Antwort, dann lässt es sich hier übernehmen"
        >
          Angebot{marke.supplierName ? ` von ${marke.supplierName}` : ''} gelesen — an der Preisanfrage bestätigen
        </Link>
      </>
    )
  }
  let text: string | null = null
  let reiter = 'material'
  if (marke.state === 'SKIPPED') text = 'nicht anfragen'
  else if (marke.state === 'DRAFT') text = `Anfrage im Entwurf${marke.supplierName ? ` an ${marke.supplierName}` : ''}`
  else if (marke.state === 'REQUESTED') {
    text = `angefragt bei ${marke.supplierName ?? '…'}${marke.dueAt ? ` · Frist ${tag(marke.dueAt)}` : ''}${marke.notOfferedBy?.length ? ` · nicht angeboten: ${marke.notOfferedBy.join(', ')}` : ''}`
    reiter = 'anfragen'
  } else if (marke.state === 'APPLIED') {
    text = `aus Angebot ${marke.documentNumber ?? ''}`.trim()
    reiter = 'anfragen'
  }
  if (!text) return null
  return (
    <>
      {' · '}
      <Link
        to={`/calculations/material-requests?tab=${reiter}&calculationId=${kalkulationId}`}
        className={`rounded px-1 ${marke.overdue ? 'bg-destructive/15 text-destructive' : 'bg-muted text-foreground'} hover:underline`}
      >
        {marke.overdue ? `${text} · überfällig` : text}
      </Link>
    </>
  )
}

/**
 * Detailblatt N 4.2/9. — der **Rechenweg** eines Arbeitsschritts: Größen eingesetzt, Ergebnis
 * vom Server, „geändert", wenn die Stunden danach von Hand überschrieben wurden. Aus einem
 * Richtwert lässt sich die Menge der Bezugsgröße hier ändern (die Stunden rechnet der Server neu).
 */
function RechenwegZeile({
  kalkulationId,
  positionId,
  schritt,
  darf,
  onGeaendert,
}: {
  kalkulationId: string
  positionId: string
  schritt: CalcWorkStep
  darf: boolean
  onGeaendert: () => void
}) {
  const weg = schritt.derivation!
  const n = weg.groessen?.n
  const ausRichtwert = weg.groessen?.r?.quelle?.art === 'RICHTWERT'
  const [menge, setMenge] = useState(n ? String(n.wert).replace('.', ',') : '')
  const [fehler, setFehler] = useState<string | null>(null)
  const nWert = n?.wert
  useEffect(() => setMenge(nWert !== undefined ? String(nWert).replace('.', ',') : ''), [nWert])
  async function speichere() {
    const wert = Number(menge.replace(/\./g, '').replace(',', '.'))
    if (!n || !Number.isFinite(wert) || wert < 0 || wert === n.wert) return
    try {
      setFehler(null)
      await api.patch(workStepUrl(kalkulationId, positionId, schritt.id), { referenceQuantity: wert }, mitStand(schritt.updatedAt))
      onGeaendert()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Menge ließ sich nicht speichern.'))
    }
  }
  return (
    <span className="flex flex-wrap items-center gap-2 px-1.5 py-1 text-xs text-muted-foreground">
      <span className="tabular-nums">{rechenwegText(weg, 'h')}</span>
      {weg.bezug === 'PER_UNIT' && <span>je Einheit</span>}
      {ausRichtwert && weg.groessen.r.quelle.name && <span>· Richtwert „{weg.groessen.r.quelle.name}"</span>}
      {weg.geaendert && <span className="rounded bg-amber-500/15 px-1 text-amber-700 dark:text-amber-400">geändert</span>}
      {ausRichtwert && darf && n && (
        <label className="flex items-center gap-1">
          Menge {n.einheit ?? ''}
          <input
            className={`${zelle} !w-20 text-right`}
            inputMode="decimal"
            value={menge}
            onChange={(e) => setMenge(e.target.value)}
            onBlur={() => void speichere()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              if (e.key === 'Escape') setMenge(String(n.wert).replace('.', ','))
            }}
          />
        </label>
      )}
      {fehler && <span className="text-destructive">{fehler}</span>}
    </span>
  )
}
